/**
 * Door / Window Tool
 *
 * Always-on selection tool for placing and editing door/window measurements
 * on the floorplan skeleton. Replaces the old DoorWindowDetectionTool's
 * interactive UX (clickable 3D boxes, hover, box-select-of-boxes, debug
 * widget). The detection algorithm itself lives in `DoorWindowDetectionService`
 * and is invoked here on tool enable + via the context-menu re-detect action.
 *
 * UX:
 *  - Hover a skeleton edge → it highlights, click to select.
 *  - Hover an existing door/window AreaAnnotation → it highlights, click to select.
 *  - Drag an OPENING_SKETCH door/window (glyph or wall line) → it slides
 *    along its wall, exactly like the layout tools' slide — no prior
 *    selection required. Legacy polygon annotations keep gizmo-only editing.
 *  - Annotations win the tie when an annotation polygon overlaps an edge.
 *  - Plain click replaces the selection; Shift adds; Ctrl/Cmd toggles.
 *  - Empty-space click clears (no modifier).
 *  - Box-drag in 2D selects every edge + annotation that overlaps the rect.
 *  - On selection-change, the context menu pops next to the cursor:
 *      • edges only (1)            → Place door / Place window
 *      • annotations only          → Convert to door, Convert to window, Delete
 *      • mixed                     → Delete (edges deselected on confirm)
 *  - Esc clears selection.
 *
 * Unlike `WallSketchEditSession`, this tool has NO `enterEditMode`/`exitEditMode`
 * — selection is live the moment the tool is enabled. We don't need the
 * skinning override / helpers group / referencePoint / originalData snapshot
 * that the skeleton session uses; door/window selection has no sub-cm-precision
 * drags and no "ESC reverts everything" semantic.
 */

import * as THREE from 'three';
import { FloorplanToolBase } from './FloorplanToolBase';
import { FloorplanPersona } from '../../personas/implementations/FloorplanPersona';
import { DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig, ToolEvents } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraMode } from '../../../defines';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { OpeningAnnotation, isDoorWindowAnnotation } from '../../../utils/PointoramaAnnotation';
import { OpeningKind } from 'shared-types';
import {
  getOpeningKindProperty,
  getOpeningWallSketchEdgeIdProperty,
  getAnnotationNameProperty,
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchWallEdgeIdProperty,
} from '@pointorama/potree-shared';
import { Snapshot } from '../ToolUndoRedoManager';
import { PixelHitTester } from '../modules/selection/PixelHitTester';
import { OpeningFlipGizmoInteraction } from '../modules/openingGizmos/OpeningFlipGizmos';
import { BoxSelectController } from '../modules/selection/BoxSelectController';
import { WallSketchSelection } from '../modules/selection/WallSketchSelection';
import { ContextMenu, ContextMenuItem, ICON_DOOR_SVG, ICON_WINDOW_SVG } from '../modules/contextMenu/ContextMenu';
// Detection / auto-materialization is temporarily disabled — the algorithm in
// `DoorWindowDetectionService` is preserved on disk but no longer invoked from
// this tool. Wall click still routes through `enterDrawPhase` so the user
// draws openings manually via the nested LineTool.
import type { RenderViewState, TransientStateSnapshot } from '../../../viewer/RenderViewState';
import { createWallFocusPreview, focusOnWall, focusOnWall3D, previewOnWall, clearPreview, WALL_FOCUS_VIEW_ID, WallFocusPreview, WallFocusSpec } from '../modules/WallFocus';
import { RoomEdgeHoverOverlay, RoomEdgePick, buildWallFocusSpecFromRoomEdgePick, collectRoomEdges, collectOutlineEdges, materializeWallEdgeFromRoomPick as materializeLooseWallEdge, pickRoomEdgeAtPixel } from '../modules/RoomEdgeFallback';
import { LineTool } from './LineTool';
import { setMeasurementSphereScale } from './MeasureSubTool';
import { ISubTool } from '../../SubTool';
import { DoorWindowEditKindAdapter } from './DoorWindowEditKindAdapter';
import {
  createOpeningToggleSnapshot,
  createOpeningGeometryEditSnapshot,
  createOpeningOffsetMoveSnapshot,
  cursorOffsetOnOpeningEdge,
  clampOpeningOffsetToClearSpan,
  WallHoleRebuildScheduler,
} from '../modules/openingSketch/OpeningSketchShared';
import { OpeningSketchElevationOverlay } from '../modules/openingSketch/OpeningSketchElevation';
import { SubToolHost } from '../../SubToolHost';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { ClipTask } from '../../../defines';
import { OpeningGizmoController } from '../modules/openingGizmos/OpeningGizmoController';

/** Pixel distance from cursor to a projected edge segment for hover to engage. */
const HOVER_EDGE_RADIUS_PX = 12;

/** Hover-preview throttle: ~30Hz so a fast mousemove doesn't thrash the widget. */
const WALL_HOVER_THROTTLE_MS = 33;

/**
 * Frozen snapshot of an OpeningAnnotation's state, captured at creation /
 * deletion time. The annotation instance itself is NOT retained — it can be
 * disposed by `removeMeasurement` between snapshots, so we serialize what we
 * need to reconstruct one (identifier, points, kind, skeleton link, group,
 * name). Plain object so it's trivially deep-copied and immune to mutations
 * on the live annotation after the snapshot was taken.
 */
interface OpeningState {
  identifier: string;
  points: { x: number; y: number; z: number }[];
  kind: OpeningKind;
  wallSketchEdgeId: string | null;
  groupIdentifier: string | null;
  name: string;
}

/**
 * Read the current state of an OpeningAnnotation in the scene into a plain
 * `OpeningState`. Used at create / delete time to snapshot what undo/redo
 * needs to round-trip. Returns null when the annotation is missing required
 * reactive properties (e.g. created before the global property service
 * initialised — defensive, shouldn't happen in normal phase-2 use).
 */
function captureOpeningState(annotation: OpeningAnnotation, groupIdentifier: string | null): OpeningState | null {
  let kind: OpeningKind = OpeningKind.WINDOW;
  let wallSketchEdgeId: string | null = null;
  try {
    kind = getOpeningKindProperty(annotation.identifier).value;
  } catch {
    /* defensive — property service not initialised */
  }
  try {
    wallSketchEdgeId = getOpeningWallSketchEdgeIdProperty(annotation.identifier).value || null;
  } catch {
    /* defensive */
  }
  let name = kind === OpeningKind.WINDOW ? annotation.viewer.translationService.t('Window') : annotation.viewer.translationService.t('Door');
  try {
    name = getAnnotationNameProperty(annotation.identifier).value || name;
  } catch {
    /* defensive */
  }
  return {
    identifier: annotation.identifier,
    points: annotation.points.map((p) => ({ x: p.position.x, y: p.position.y, z: p.position.z })),
    kind,
    wallSketchEdgeId,
    groupIdentifier,
    name,
  };
}

/**
 * Reconstruct an OpeningAnnotation in the scene from a captured state and
 * dispatch `measurement_finish` so the React layer's `onMeasurementFinish`
 * handler re-creates the Apollo / GraphQL record. Mirrors the path
 * `DoorWindowEditKindAdapter.commitNew` takes when the user first draws
 * the opening — only difference is we restore the original identifier so
 * the annotation keeps its identity across undo/redo cycles.
 */
function recreateOpeningInScene(viewer: Viewer, state: OpeningState): void {
  const measure = new OpeningAnnotation({
    viewer,
    name: state.name,
    identifier: state.identifier,
    points: state.points.map((p) => ({ position: new THREE.Vector3(p.x, p.y, p.z), pointCloudId: '' })),
  });
  try {
    getOpeningKindProperty(measure.identifier).value = state.kind;
  } catch {
    /* defensive */
  }
  if (state.wallSketchEdgeId) {
    try {
      getOpeningWallSketchEdgeIdProperty(measure.identifier).value = state.wallSketchEdgeId;
    } catch {
      /* defensive */
    }
  }
  viewer.scene.addMeasurement(measure);
  viewer.scene.finishMeasurement(measure, { groupIdentifier: state.groupIdentifier, skipSelection: true });
}

/**
 * Remove an OpeningAnnotation from the scene + dispatch `measurement_delete`
 * so the React layer's `onMeasurementDeleteAnnotation` handler clears the
 * Apollo / GraphQL record. Lookup is by identifier so this works even when
 * the live measurement instance has been replaced by a recreate.
 */
function deleteOpeningFromScene(viewer: Viewer, identifier: string): void {
  const live = viewer.scene.measurements.find((m) => m.identifier === identifier);
  if (live) viewer.scene.removeMeasurement(live);
  viewer.scene.dispatchEvent({
    type: 'measurement_delete',
    value: { measure: { identifier } },
  });
}

/**
 * Undo/redo snapshot for the existence of one or more OpeningAnnotations.
 * Toggles between "openings exist" and "openings absent" — used for both
 * the create flow (initial state = exist; restore() deletes, second
 * restore() recreates) and the delete flow (initial state = absent;
 * restore() recreates, second restore() deletes). Captured state is fully
 * frozen as plain JSON-like data so a removed-then-disposed annotation
 * is still recoverable.
 */
class OpeningExistenceSnapshot implements Snapshot {
  /** false = next restore deletes (initial state: openings exist); true = next restore recreates. */
  private deletedNext: boolean;

  constructor(
    private readonly viewer: Viewer,
    private readonly states: OpeningState[],
    initialState: 'exists' | 'absent',
  ) {
    // `exists` means the next restore removes them (undo of a create);
    // `absent` means the next restore recreates them (undo of a delete).
    this.deletedNext = initialState === 'exists';
  }

  /**
   * True when calling `restore()` next would DELETE openings — i.e. this
   * snapshot is sitting on the undo stack and represents an outstanding
   * "create" the user could roll back. The Esc handler reads this to
   * decide whether Esc should undo (one chance to revert a fresh
   * creation) before tearing down wall focus.
   */
  get nextRestoreDeletes(): boolean {
    return this.deletedNext;
  }

  restore(): Snapshot {
    if (this.deletedNext) {
      for (const s of this.states) deleteOpeningFromScene(this.viewer, s.identifier);
    } else {
      for (const s of this.states) recreateOpeningInScene(this.viewer, s);
    }
    this.deletedNext = !this.deletedNext;
    return this;
  }
}

/**
 * Undo/redo snapshot for kind toggles (door ↔ window). Flips the reactive
 * `opening_kind` property for each captured identifier; the React layer's
 * subscription on that property handles persisting the change to the
 * Apollo / GraphQL record, so no scene re-add is needed.
 */
class OpeningKindSnapshot implements Snapshot {
  /** false = next restore applies `after` (initial state right after the user toggled); true = next restore applies `before`. */
  private applyBefore = true;

  constructor(private readonly entries: { identifier: string; before: OpeningKind; after: OpeningKind }[]) {}

  restore(): Snapshot {
    for (const e of this.entries) {
      try {
        getOpeningKindProperty(e.identifier).value = this.applyBefore ? e.before : e.after;
      } catch {
        /* defensive — property service not initialised */
      }
    }
    this.applyBefore = !this.applyBefore;
    return this;
  }
}

/** Camera snapshot preserved across a magic <-> manual door/window hand-off. */
interface DoorWindowCameraState {
  position: THREE.Vector3;
  target: THREE.Vector3;
  radius: number;
}

export class DoorWindowTool extends FloorplanToolBase {
  readonly name: string = 'door_window';

  /**
   * The two door/window tool ids. Switching between them (magic <-> manual)
   * preserves the phase-2 wall focus instead of tearing it down: the outgoing
   * tool stashes its wall spec on the persona, the incoming tool restores it.
   */
  protected static readonly DOOR_WINDOW_TOOL_IDS = ['door_window', 'manual_door_window'] as const;

  /** Active phase-2 wall spec (set on enter, cleared on teardown). */
  private activeWallSpec: WallFocusSpec | null = null;

  private readonly hitTester: PixelHitTester;
  /** Door/window flip gizmos for a single selected opening (shared UX).
   *  Selection stays suppressed for this tool's whole lifetime, so the
   *  interaction skips its own suppression management. */
  private flipInteraction: OpeningFlipGizmoInteraction;

  // WallSketch edge selection is owned by the active PointoramaWallSketch itself;
  // we proxy through `edgeSelection` (below) which resolves to whatever the
  // current floor's skeleton has. Door/window annotation selection sits next
  // to it as a separate id set; the two move together but only edges have
  // skeleton-side visuals.
  private selectedAnnotationIds: Set<string> = new Set();

  // Hover state — refreshed on every mousemove. Click handlers rely on it
  // and on the click-target snapshot so a drag that drifts off the target
  // still acts on what the user pressed on.
  private hoveredEdgeId: string | null = null;
  private hoveredAnnotationId: string | null = null;
  private clickTargetEdgeId: string | null = null;
  private clickTargetAnnotationId: string | null = null;

  /** Drag state for sliding an existing OPENING_SKETCH along its wall — a
   *  body drag on the door/window glyph, mirroring the sketch-object tool's
   *  slide (no prior selection required). Phase-1 only; in phase-2 the inner
   *  LineTool owns opening edits. */
  private openingSlide: { openingId: string; edgeId: string; grabDelta: number; beforeOffset: number; moved: boolean } | null = null;
  /** rAF-coalesced wall-hole re-cut while an opening slides. */
  private holeRebuild = new WallHoleRebuildScheduler(() => this.getCurrentFloorWallSketch());

  // No-skeleton fallback hover state. Mutually exclusive with `hoveredEdgeId` —
  // the fallback path only activates when `getCurrentFloorWallSketch()` is null,
  // so a non-null pick here implies the skeleton-edge path didn't run. The
  // click handler reads this snapshot to enter wall-focus via the same
  // `autoMaterializeAndFocusWall` flow the skeleton path uses.
  private hoveredRoomPick: RoomEdgePick | null = null;
  private roomEdgeOverlay: RoomEdgeHoverOverlay | null = null;

  /**
   * Last floor group identifier seen by `onFloorChanged`. `FloorplanToolBase`
   * fires `onFloorChanged` on every `objects_selection_changed` event — so
   * selecting an opening inside the current floor (sidebar click, etc.) would
   * otherwise tear down phase-2 wall focus. We compare against this snapshot
   * to skip the teardown when the floor didn't actually change.
   */
  private lastFloorGroupIdentifier: string | null = null;

  private boxSelect: BoxSelectController | null = null;
  private contextMenu: ContextMenu | null = null;

  // Widget wall-focus state. The preview handle survives across hover frames
  // so the snapshot taken on first hover is what unhover restores to. Cleared
  // on disable / floor change. Hover updates are throttled to ~30Hz to keep
  // the widget responsive during fast mouse motion across many edges.
  private readonly wallFocusPreview: WallFocusPreview = createWallFocusPreview();
  private lastHoverFocusedEdgeId: string | null = null;
  private lastHoverApplyTime = 0;
  /**
   * Wall key of the click-owned widget focus (3D perspective view), or null.
   * While set, hover over the SAME wall must not re-apply the ortho X-Ray
   * preview — the hover handler fires on every mousemove over the floor plan
   * and would otherwise stomp the selected wall's 3D view within one
   * throttle interval of the click.
   */
  private widgetClickFocusKey: string | null = null;

  /**
   * Preview handle for the MAIN view's wall focus. Wired the same way the
   * widget's `wallFocusPreview` is — `focusOnWall(mainViewState, spec, this.mainWallFocusPreview)`
   * creates/promotes a persistent `Wall focus` tab on the main view's
   * `RenderViewState` and frames the wall via the shared `applyWallFocus`
   * math. Cleared on disable / floor change so the tab tears down with the tool.
   */
  private readonly mainWallFocusPreview: WallFocusPreview = createWallFocusPreview();

  /**
   * Phase-2 LineTool nested inside this tool. Lazily constructed on first
   * entry so the phase-1 selection workflow doesn't pay the LineTool setup
   * cost up front. Driven through `enable()` / `disable()` for clean DOM-
   * listener handoff; while it's enabled, mouse + keyboard events go to
   * the inner tool, NOT this outer one.
   */
  // The phase-2 draw subtool. Typed as the neutral ISubTool so a subclass can
  // swap the engine (the magic flow uses a LineTool; ManualDoorWindowTool uses
  // a MeasureSubTool). Created by the `createDrawSubTool` factory.
  protected innerSubTool: ISubTool | null = null;

  /**
   * Single adapter instance shared between the inner draw subtool (which reads
   * `kindAdapter.polygonPipelineConfigPath` etc.) and the selection context
   * the host pushes the base plane from. Must be one instance so the context
   * the host attaches is the same one the subtool's commit path consults.
   */
  protected readonly doorWindowAdapter = new DoorWindowEditKindAdapter();

  /**
   * Elevation rectangles for the OPENING_SKETCHes on the focused wall chain —
   * their plan visuals (glyph + footprint line) are edge-on in the side view,
   * so this overlay is what makes committed openings visible (and, via the
   * adapter's hit-test, click-editable) in the wall view. Parented into the
   * uiScene lazily; refreshed per frame while a wall is focused.
   */
  private openingElevationOverlay: OpeningSketchElevationOverlay | null = null;
  private openingElevationGroup: THREE.Group | null = null;
  /** Edge ids of the focused collinear chain (mirrors the adapter's focus
   *  context, for the overlay refresh). Empty when no wall is focused. */
  private focusedChainEdgeIds: string[] = [];

  private ensureOpeningElevationOverlay(): OpeningSketchElevationOverlay {
    if (!this.openingElevationOverlay) {
      this.openingElevationOverlay = new OpeningSketchElevationOverlay(this.viewer, () => {
        if (!this.openingElevationGroup) {
          this.openingElevationGroup = new THREE.Group();
          this.openingElevationGroup.name = 'door_window_opening_elevation';
          this.viewer.uiScene.add(this.openingElevationGroup);
        }
        return this.openingElevationGroup;
      });
      this.doorWindowAdapter.elevationOverlay = this.openingElevationOverlay;
    }
    return this.openingElevationOverlay;
  }

  /** Per-frame: keep the elevation rectangles in sync with the focused chain
   *  and the openings' live parameters (cheap signature check inside). */
  private readonly refreshOpeningElevationOverlay = (): void => {
    const overlay = this.ensureOpeningElevationOverlay();
    if (!this.activeWallSpec || this.focusedChainEdgeIds.length === 0) {
      overlay.hide();
      return;
    }
    overlay.refresh(this.getCurrentFloorWallSketch(), this.focusedChainEdgeIds);
  };

  private disposeOpeningElevationOverlay(): void {
    this.openingElevationOverlay?.dispose();
    this.openingElevationOverlay = null;
    this.doorWindowAdapter.elevationOverlay = null;
    if (this.openingElevationGroup) {
      this.viewer.uiScene.remove(this.openingElevationGroup);
      this.openingElevationGroup = null;
    }
  }

  /**
   * Controller that owns the inner LineTool's lifecycle + base-plane wiring.
   * Constructed lazily alongside the LineTool in `ensureInnerLineTool`. Input
   * in phase-2 is forwarded into this host (the subtool no longer binds its
   * own DOM listeners).
   */
  private innerHost: SubToolHost | null = null;

  /** Unsubscribe from the current floor's skeleton selection events. */
  private unsubscribeSelection: (() => void) | null = null;

  /**
   * Snapshot of `viewer.viewerMode.associatedPlane` before we swapped it to the
   * wall plane on focus. The LineTool's `CoordinateTransformer.screenToWorld`
   * unprojects onto `viewerMode.associatedPlane`; for phase-2 we need it set
   * to the vertical wall plane so prompts, segmentation captures, and click
   * positions live on the wall. Restored verbatim when the wall focus tears
   * down.
   */
  private previousAssociatedPlane: PointoramaPlane | null = null;
  private wallPlaneActive = false;

  /**
   * Gizmo controller for the phase-1 single-selection editing UX
   * (translate, rotate, align). Owned for the tool's lifetime — its
   * `attach` / `detach` follow selection changes. Hidden (not detached)
   * while phase-2 wall focus is active so the user's selection survives
   * the round-trip.
   */
  private openingGizmos: OpeningGizmoController;

  /**
   * Subscription that watches `mainState.currentViewId` for the wall
   * focus tab being deactivated. Without this, a user clicking an X-Ray
   * or 3D tab in the corner while wall focus is active would atomically
   * switch the view but leave the inner LineTool, the wall plane swap,
   * and the LineTool widget hanging — only the `mainState.setView`
   * inside `clearMainWallView` cleans those up, and the tab UI bypasses
   * that. Installed in `autoMaterializeAndFocusWall` after focus is
   * applied; disposed in `clearMainWallView`.
   */
  private wallFocusViewWatcher: { unsubscribe: () => void } | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.hitTester = new PixelHitTester(viewer);
    // Init undo/redo in the constructor — `BaseTool.enable()` calls
    // `registerEventListeners()` BEFORE `onEnable()`, and the registration
    // is gated on `this._undoRedo` already being set. Initializing in
    // `onEnable` would mean the Ctrl+Z/Y key handler is never registered
    // on `window`, so the outer tool would never receive undo keys and
    // the inner LineTool would always win.
    this.initUndoRedo();
    this.flipInteraction = new OpeningFlipGizmoInteraction({
      viewer,
      hitTester: this.hitTester,
      undoRedo: () => this.undoRedo ?? null,
      getWallSketch: () => (this.currentFloorGroupIdentifier ? this.viewer.scene.wallSketches.find((sk) => sk.groupIdentifier === this.currentFloorGroupIdentifier) ?? null : null),
      // Hidden in phase-2: the wall-focus elevation view lies edge-on to the
      // floor-plane gizmos.
      shouldShow: () => !this.innerLineToolActive,
      manageSelectionSuppression: false,
    });
    this.openingGizmos = new OpeningGizmoController({
      viewer,
      undoRedo: () => this.undoRedo,
      getRooms: () => this.getRoomsOnCurrentFloor(),
      getOutlines: () => this.getOutlinesOnCurrentFloor(),
    });
    // Phase-2 commits create OPENING_SKETCHes (see the adapter's commitNew);
    // push the existence-toggle undo snapshot and drop the inner LineTool's
    // pre-commit prompt history — same rationale as handleMeasurementFinish
    // for the legacy annotation path.
    this.doorWindowAdapter.onOpeningSketchCommitted = (request) => {
      this.undoRedo?.pushState(createOpeningToggleSnapshot(this.viewer, request, true));
      this.clearDrawSubToolUndo();
    };
    // Elevation rectangle edits (move/resize an existing OPENING_SKETCH in
    // the wall view) get a geometry-toggle undo snapshot.
    this.doorWindowAdapter.onOpeningSketchEdited = (openingId, before, after) => {
      this.undoRedo?.pushState(createOpeningGeometryEditSnapshot(() => this.getCurrentFloorWallSketch(), openingId, before, after));
      this.clearDrawSubToolUndo();
    };
  }

  /**
   * Selection is owned by the active floor's PointoramaWallSketch — this getter
   * resolves to that selection, or null when no skeleton is available.
   */
  private get edgeSelection(): WallSketchSelection | null {
    return this.getCurrentFloorWallSketch()?.getSelection() ?? null;
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  protected onEnable(): void {
    super.onEnable();
    this.flipInteraction.attach();
    // Opening annotations live in scene.measurements — full-size vertex
    // spheres while a door/window tool is active, half size outside.
    setMeasurementSphereScale(this.viewer, 1);
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    // Suppress the core InputHandler's legacy `selectionTool` path. It runs
    // on every mousemove and click and would otherwise (a) force the cursor
    // back to 'default' via `Utils.changeCursor`, overriding our pointer
    // hover, and (b) dispatch `measurement_select` with `null` on every
    // click — clobbering the annotation selection we dispatch from
    // `onClick`. This tool owns wall + door/window selection entirely while
    // it's active.
    this.viewer.inputHandler.selectionDisabled = true;
    // `Utils.changeCursor` (used by the legacy selectionTool we just gated)
    // writes to `document.body.style.cursor`. If it last left the body
    // cursor at 'default' or 'pointer', that overrides our 'crosshair' on
    // the canvas. Clear it so our canvas cursor wins.
    document.body.style.cursor = '';
    // Outer-tool undo/redo for door/window existence (create / delete) and
    // kind toggles. The manager itself is initialized in the constructor
    // (`registerEventListeners()` reads it before `onEnable` runs); here
    // we just clear it so a fresh enable starts with an empty stack.
    this.undoRedo?.clear();
    // Watch for new OpeningAnnotations finishing. Phase-2 commits flow
    // through the inner LineTool's `kindAdapter.commitNew` →
    // `addMeasurement` + `finishMeasurement`; the resulting
    // `measurement_finish` event is our cleanest hook to push an undo
    // snapshot without changing the adapter signature. The handler
    // self-gates against undo/redo `restoring` mode via the manager
    // (`pushState` is a no-op while `restoring` is true), so re-creates
    // dispatched during redo don't grow the stack.
    this.viewer.scene.addEventListener('measurement_finish', this.handleMeasurementFinish);
    // Elevation rectangles for committed openings follow the camera + live
    // property values while a wall is focused (no-op signature check per frame).
    this.viewer.addEventListener('update', this.refreshOpeningElevationOverlay);
    this.dispatchStateChanged({ phase: 'idle' });
    this.subscribeToWallSketchSelection();
    // If the sibling door/window tool handed off an active wall focus (magic
    // <-> manual switch), restore phase-2 on that wall + view synchronously.
    // Doing it inline (no setTimeout) keeps the wall-focus state continuous
    // across the swap — the persona's `enableTool` finishes inside the same
    // task that called this onEnable, so any render between here and the end
    // of the tool switch would otherwise see a half-torn-down wall focus
    // (associatedPlane reverted, floor isolation re-applied) and flash the
    // default top-down floor view for one frame.
    this.restorePendingWallFocus();
  }

  /**
   * Capture an undo snapshot when a new OpeningAnnotation finishes in the
   * scene while this tool owns the editing context. The snapshot's
   * `restore()` deletes the annotation on undo and recreates it on redo.
   */
  private handleMeasurementFinish = ({ value }: { value?: { measure: { identifier: string } | OpeningAnnotation; groupIdentifier?: string | null } }): void => {
    if (!value) return;
    const measure = value.measure;
    if (!(measure instanceof OpeningAnnotation)) return;
    const state = captureOpeningState(measure, value.groupIdentifier ?? this.currentFloorGroupIdentifier);
    if (!state) return;
    this.undoRedo?.pushState(new OpeningExistenceSnapshot(this.viewer, [state], 'exists'));
    // Drop the inner LineTool's pre-commit prompt snapshots — once the
    // opening is committed, the outer undo stack owns "delete this
    // opening" as the meaningful Ctrl+Z. Otherwise a post-commit Ctrl+Z
    // would fall through to the inner's stack (after the outer pops the
    // existence snapshot) and re-enter shape-edit mode on the prompts
    // that built the now-deleted opening.
    this.clearDrawSubToolUndo();
  };

  /** Clear the draw subtool's own undo stack (LineTool has one; MeasureSubTool doesn't). */
  protected clearDrawSubToolUndo(): void {
    this.innerSubTool?.undoRedo?.clear();
  }

  /**
   * Undo precedence: the inner draw subtool's stack is consumed first, but ONLY
   * while it's actively editing (drawing in progress / prompts on screen / an
   * editing shape). The LineTool keeps prompt-undo history even when idle, so
   * gating on a non-empty stack alone would let Ctrl+Z dive back into the inner
   * stack and re-enter edit mode on stale prompts instead of undoing the outer
   * opening create/delete/convert. `isDrawSubToolBusy()` is the "are we mid-edit"
   * signal; only then do we delegate. Otherwise the outer stack handles it.
   */
  override performUndo(): boolean {
    if (this.shouldConsumeInnerUndo() && this.innerSubTool?.undoRedo?.canUndo) {
      return this.innerSubTool.performUndo?.() ?? false;
    }
    return super.performUndo();
  }

  override performRedo(): boolean {
    if (this.shouldConsumeInnerUndo() && this.innerSubTool?.undoRedo?.canRedo) {
      return this.innerSubTool.performRedo?.() ?? false;
    }
    return super.performRedo();
  }

  /**
   * Whether the inner subtool's undo stack should be consumed before the outer
   * one. For the LineTool (magic) the inner stack holds PRE-COMMIT prompt states
   * that go stale after a commit, so we only delegate while it's actively
   * editing (`isDrawSubToolBusy`) — otherwise Ctrl+Z would re-enter edit mode on
   * stale prompts instead of undoing the opening create. `ManualDoorWindowTool`
   * (MeasureEngine) overrides this: each inner entry is a committed point-edit
   * that's always meaningful, so it delegates whenever the stack is non-empty.
   */
  protected shouldConsumeInnerUndo(): boolean {
    return this.isDrawSubToolBusy();
  }

  /**
   * True iff the phase-2 draw subtool has in-progress work that should claim
   * Esc/Enter/Ctrl+Z before the outer tool. For the LineTool that means active
   * prompts or an editing shape; `ManualDoorWindowTool` overrides this for the
   * MeasureEngine's "drawing in progress" state.
   */
  protected isDrawSubToolBusy(): boolean {
    if (!this.innerLineToolActive) return false;
    const mode = this.asLineTool()?.getActiveMode();
    if (!mode) return false;
    return mode.isEditingShape() || mode.getLinePrompts().length > 0;
  }

  /** The inner subtool as a LineTool, or null if it isn't one (manual subclass). */
  protected asLineTool(): LineTool | null {
    return this.innerSubTool instanceof LineTool ? this.innerSubTool : null;
  }

  /** (Re)subscribe to the active skeleton's selection-changed event. */
  private subscribeToWallSketchSelection(): void {
    if (this.unsubscribeSelection) {
      this.unsubscribeSelection();
      this.unsubscribeSelection = null;
    }
    const skel = this.getCurrentFloorWallSketch();
    if (!skel) return;
    this.unsubscribeSelection = skel.subscribeSelectionChanged(() => {
      this.closeContextMenuIfEmpty();
    });
  }

  protected onDisable(): void {
    this.flipInteraction.detach();
    setMeasurementSphereScale(this.viewer, 0.5);
    this.viewer.renderer.domElement.style.cursor = '';
    this.viewer.inputHandler.selectionDisabled = false;
    this.viewer.scene.removeEventListener('measurement_finish', this.handleMeasurementFinish);
    this.viewer.removeEventListener('update', this.refreshOpeningElevationOverlay);
    this.disposeOpeningElevationOverlay();
    this.undoRedo?.clear();

    if (this.contextMenu) {
      this.contextMenu.dispose();
      this.contextMenu = null;
    }

    this.clearSelection();
    // Hand the shared selection's visuals back to other consumers (the wall
    // sketch editor renders its cyan highlight through the same object).
    this.edgeSelection?.setVisualsSuppressed(false);
    // If we're handing off to the sibling door/window tool while a wall is
    // focused, preserve the wall view + plane + isolation so the user stays on
    // the same wall in the same view; the sibling restores phase-2. The widget
    // tab is left in place too — its full RenderViewState snapshot is stashed
    // so the sibling can reseat the user's panned/orbited 3D view exactly.
    // Otherwise tear everything down (the outer stays `enabled` across
    // phase-1 → phase-2, so we only reach onDisable on a real exit:
    // persona switch, view teardown).
    if (this.shouldHandOffWallFocus()) {
      (this.persona as FloorplanPersona).setPendingDoorWindowWallFocus(this.activeWallSpec, this.captureMainCamera(), this.captureWidgetSnapshot());
      this.tearDownInnerDrawPhaseOnly();
    } else {
      this.clearWidgetWallFocus();
      this.clearMainWallView();
    }
    if (this.unsubscribeSelection) {
      this.unsubscribeSelection();
      this.unsubscribeSelection = null;
    }
    this.hoveredEdgeId = null;
    this.hoveredAnnotationId = null;
    this.hoveredRoomPick = null;
    this.clickTargetEdgeId = null;
    this.clickTargetAnnotationId = null;
    this.openingSlide = null;
    this.holeRebuild.cancel();
    if (this.roomEdgeOverlay) {
      this.roomEdgeOverlay.dispose();
      this.roomEdgeOverlay = null;
    }
    if (this.innerHost) {
      this.innerHost.deactivate();
      this.innerHost.dispose();
      this.innerHost = null;
    }
    this.innerSubTool = null;

    super.onDisable();
  }

  /**
   * Tear down the main-view wall focus tab installed by
   * `autoMaterializeAndFocusWall`. The tab was created as persistent
   * (per the design decision: main view doesn't get hover-transient
   * wall focus). Removing it restores the prior camera/clip/density
   * snapshot the state captured when the tab was created.
   *
   * Also clears the edge selection + widget wall focus that were set
   * by the phase-1 click that opened this view, so the floor-view
   * state matches the "tool freshly entered, no wall picked yet" state
   * the user sees when they exit the view. Without this, switching
   * from the wall-focus tab to X-Ray (or Esc'ing) would leave the cyan
   * edge highlight and the widget's persistent wall-focus card around.
   */
  private clearMainWallView(): void {
    this.activeWallSpec = null;
    this.doorWindowAdapter.setFocusContext(null);
    this.focusedChainEdgeIds = [];
    this.openingElevationOverlay?.hide();
    // Drop the view watcher BEFORE removing the tab so we don't fire
    // ourselves recursively via `removeCustomView`'s `currentViewId`
    // mutation.
    this.wallFocusViewWatcher?.unsubscribe();
    this.wallFocusViewWatcher = null;
    // Tear down the nested LineTool first (if active) so it commits/cancels
    // its in-flight edit before we yank the wall plane out from under it.
    this.exitDrawPhase();
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (mainState && mainState.getCustomView(WALL_FOCUS_VIEW_ID)) {
      mainState.removeCustomView(WALL_FOCUS_VIEW_ID);
    }
    this.restorePreviousAssociatedPlane();
    // Re-apply the floor-isolation slab that wall focus suspended on entry.
    this.resumeFloorIsolation();
    this.clearSelection();
    this.clearWidgetWallFocus();
    // Consumed by the exit setView above; clear so it can't leak into a later
    // focus session that fails to capture (defensive).
    this.wallFocusReturnViewId = null;
  }

  /**
   * True when this tool is being disabled to switch to the sibling door/window
   * tool (magic <-> manual) while a wall is focused — in which case we hand the
   * wall focus off instead of tearing it down.
   */
  private shouldHandOffWallFocus(): boolean {
    if (!this.activeWallSpec) return false;
    if (!(this.persona instanceof FloorplanPersona)) return false;
    const incoming = this.persona.getIncomingToolId();
    return incoming !== null && incoming !== this.name && (DoorWindowTool.DOOR_WINDOW_TOOL_IDS as readonly string[]).includes(incoming);
  }

  /**
   * Hand-off teardown: drop THIS tool's phase-2 draw subtool + view watcher and
   * restore the per-tool plane/isolation state it owns (so the snapshots don't
   * leak), while LEAVING the wall view tab in place so the sibling tool's
   * `restorePendingWallFocus` re-installs the plane + isolation from a clean
   * floor baseline without the view flickering.
   */
  private tearDownInnerDrawPhaseOnly(): void {
    this.wallFocusViewWatcher?.unsubscribe();
    this.wallFocusViewWatcher = null;
    this.exitDrawPhase();
    this.restorePreviousAssociatedPlane();
    this.resumeFloorIsolation();
    this.activeWallSpec = null;
    this.doorWindowAdapter.setFocusContext(null);
    this.focusedChainEdgeIds = [];
    this.openingElevationOverlay?.hide();
  }

  /**
   * Re-establish phase-2 wall focus from a spec the sibling tool stashed on the
   * persona (magic <-> manual switch). Restores the user's camera (position +
   * target + zoom) AFTER re-framing, so switching tools keeps the exact view —
   * not the default wall-fit framing. Returns true if a hand-off was restored.
   */
  private restorePendingWallFocus(): boolean {
    if (!(this.persona instanceof FloorplanPersona)) return false;
    const pending = this.persona.takePendingDoorWindowWallFocus();
    if (!pending) return false;
    const spec = pending.spec as WallFocusSpec | null;
    if (!spec) return false;
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (!mainState) return false;
    const cam = pending.camera as DoorWindowCameraState | null;
    // Re-apply the captured camera over the wall-fit framing — AFTER the focus
    // (and its awaited XRAY_2D switch) settles, else the switch's camera
    // conversion would land last and overwrite the restored pose.
    //
    // Guarded so the restore is cancellable: if the user switched tools or
    // views again while the focus was in flight (tool disabled, or the wall
    // spec no longer active), the stale pose write is dropped instead of
    // landing on whatever is showing now.
    void this.applyWallFocusAndEnterDrawPhase(spec, mainState).then(() => {
      if (!this.enabled || this.activeWallSpec !== spec) return;
      if (cam) {
        mainState.setCameraPose(cam.position.clone(), cam.target.clone(), CameraMode.ORTHOGRAPHIC);
        mainState.setOrbitRadius(cam.radius);
      }
    });
    // Re-seat the widget's 3D wall view from the stashed snapshot. The widget
    // tab was left in place during the outgoing tool's teardown; restoring the
    // snapshot replays the user's panned/orbited pose + overrides so the view
    // is identical across the swap. Skipped when no widget was mounted on the
    // outgoing side or the widget has since been hidden.
    const widgetSnap = pending.widget as TransientStateSnapshot | null;
    if (widgetSnap) {
      const widgetState = this.getWidgetViewState();
      if (widgetState) widgetState.restoreTransientState(widgetSnap);
    }
    return true;
  }

  /** Snapshot the main view's camera so a tool hand-off can preserve it. */
  private captureMainCamera(): DoorWindowCameraState | null {
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (!mainState) return null;
    const snap = mainState.captureTransientState();
    return {
      position: snap.cameraPosition.clone(),
      target: snap.cameraTarget.clone(),
      radius: mainState.getOrbitRadius(),
    };
  }

  /**
   * Snapshot the widget's full RenderViewState (pose, projection, overrides,
   * active view id) so a tool hand-off can restore the user's 3D wall view
   * exactly. Returns null when no widget is mounted.
   */
  private captureWidgetSnapshot(): TransientStateSnapshot | null {
    const widgetState = this.getWidgetViewState();
    if (!widgetState) return null;
    return widgetState.captureTransientState();
  }

  // ─── Phase 2 — opening edit ─────────────────────────────────────────
  // The user has clicked a wall (phase-1), detection materialized
  // openings on the strip, and the main view is now framed on that wall.
  // Clicking an existing door/window AreaAnnotation enters phase-2:
  //
  //  - Set `innerLineToolActive` so this outer tool's event handlers
  //    short-circuit (the outer stays `enabled` so the persona's
  //    tool-switch teardown still finds and disables it).
  //  - Enable the nested LineTool with `DoorWindowEditKindAdapter`.
  //  - Drive the inner LineTool's active mode into
  //    `enterShapeEditMode(annotation)` so its prompt-edit machinery
  //    operates on the clicked rectangle.
  //
  // Esc or Enter from phase-2 exits the inner LineTool's edit mode,
  // disables the inner tool, and re-enables this outer one — control
  // returns to phase-2 idle (wall view, click another opening to edit).

  /**
   * Build the phase-2 draw subtool. Base = the shared LineTool (magic +
   * manual line modes). `ManualDoorWindowTool` overrides to return a
   * MeasureSubTool (click-to-place). The same `doorWindowAdapter` instance
   * drives commit + the selection context, so openings commit identically
   * regardless of engine.
   */
  protected createDrawSubTool(): ISubTool {
    return new LineTool(this.viewer, this.persona, undefined, this.doorWindowAdapter);
  }

  /** Lazy-init the inner draw subtool + its host the first time we enter phase-2. */
  private ensureInnerLineTool(): ISubTool {
    if (!this.innerSubTool) {
      this.innerSubTool = this.createDrawSubTool();
      const ctx = this.doorWindowAdapter.createSelectionContext(this.viewer, this.persona);
      this.innerHost = new SubToolHost(ctx);
      this.innerHost.addSubTool(this.innerSubTool);
      // Reflect the inner stack in the toolbar's undo/redo button state: when
      // the inner subtool's stack changes, re-emit the combined (inner || outer)
      // availability so the button stays enabled while either stack has history.
      const innerMgr = this.innerSubTool.undoRedo;
      if (innerMgr) {
        const prev = innerMgr.onStackChanged;
        innerMgr.onStackChanged = () => {
          prev?.();
          this.dispatchUndoRedoChanged();
        };
      }
    }
    return this.innerSubTool;
  }

  /**
   * Report the COMBINED inner-subtool + outer availability so the toolbar's
   * undo/redo buttons stay enabled while either stack has history (undo
   * consumes the inner stack first; see performUndo).
   */
  protected override dispatchUndoRedoChanged(): void {
    // Mirror performUndo's gate so the button state matches what Ctrl+Z does.
    const innerMgr = this.shouldConsumeInnerUndo() ? this.innerSubTool?.undoRedo : null;
    const canUndo = (innerMgr?.canUndo ?? false) || (this.undoRedo?.canUndo ?? false);
    const canRedo = (innerMgr?.canRedo ?? false) || (this.undoRedo?.canRedo ?? false);
    const event = { type: ToolEvents.UNDO_REDO_CHANGED, tool: this, canUndo, canRedo };
    this.dispatchEvent(event);
    this.viewer.scene.dispatchEvent(event);
  }

  /**
   * Phase-2 is active iff the inner LineTool is currently enabled. The
   * outer tool stays `enabled` while this is true — its event handlers
   * gate themselves on this flag so phase-1 selection logic doesn't fire
   * on top of the inner tool's drawing/editing work. We keep the outer
   * enabled (instead of disabling it on entry) so that a persona-level
   * tool switch mid-phase-2 still routes through `onDisable` and tears
   * the wall focus down.
   */
  protected innerLineToolActive = false;

  /**
   * Esc/Enter listener installed while phase-2 is active. The outer tool
   * is `disable()`'d in phase-2 so its `onKeyDown` doesn't fire; the
   * inner LineTool's onKeyDown intercepts Esc/Enter when it has prompts
   * or an editing shape (and stopPropagation's them). When the inner
   * tool is idle, Esc reaches this listener and we tear the wall focus
   * down.
   */
  private wallFocusEscListener: ((e: KeyboardEvent) => void) | null = null;

  /**
   * Enter phase-2: nest the LineTool so it handles all drawing + edit
   * input on the wall view. Plain idle click → draw new opening
   * (commitNew creates a WINDOW; user converts via context menu). Click
   * on existing AreaAnnotation → edit mode on that rectangle. Esc/Enter
   * inside the LineTool's edit mode cancels/commits the edit; the outer
   * tool's onKeyDown handles the wall-focus Esc tear-down.
   *
   * The outer tool stays `enabled` while phase-2 runs — its event
   * handlers gate themselves on `innerLineToolActive` so phase-1
   * mouse / keyboard work doesn't fire. Disabling the outer here would
   * make it invisible to the persona's tool-switch teardown, leaving
   * the wall focus / wall plane / skeleton subscription orphaned when
   * the user picks another tool mid-phase-2 (the persona only iterates
   * `getEnabledTools()` to disable).
   */
  private enterDrawPhase(): void {
    if (this.innerLineToolActive) return;
    // Build the inner LineTool + host on first entry. We don't need the
    // returned tool reference here — activation goes through the host below.
    this.ensureInnerLineTool();
    this.innerLineToolActive = true;
    // Hide phase-1 gizmos while wall focus is active. They stay attached
    // to the same selection so the user returns to a familiar state on
    // Esc/Enter.
    this.openingGizmos.setVisible(false);
    // Esc/Enter listener with explicit precedence: the inner LineTool
    // gets first dibs on Esc/Enter so it can confirm/cancel an edit,
    // finalize a new shape, or clear in-progress line prompts. Only when
    // the inner tool is genuinely idle (no editing shape, no prompts)
    // do we tear the wall focus down. Both event handlers run on
    // `window` keydown; we register late enough to be after the inner
    // tool's listener, then short-circuit if the inner tool consumed
    // the key (`defaultPrevented`) OR has state that would have made it
    // consume the key. This avoids a race where the LineTool's listener
    // hasn't installed yet at first keypress.
    this.wallFocusEscListener = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Enter') return;
      if (e.defaultPrevented) return;
      // Defer to the draw subtool while it has in-progress work.
      if (this.isDrawSubToolBusy()) return;
      e.preventDefault();
      e.stopPropagation();
      // Atomic exit via mainState.setView — see onKeyDown's comment. Return to
      // the view we came from (the wall-focus tab's captured origin), so a wall
      // focused from 3D exits back to 3D, not always to 2D X-Ray.
      const mainState = this.viewer.renderViews[0]?.viewState;
      if (mainState) mainState.setView(this.wallFocusOriginViewId() ?? ViewModes.XRAY_2D.id);
      this.clearMainWallView();
    };
    window.addEventListener('keydown', this.wallFocusEscListener);
    queueMicrotask(() => {
      this.innerHost?.activate();
    });
  }

  /**
   * Exit phase-2. Disables the inner LineTool — the outer was never
   * disabled (it gated its handlers via `innerLineToolActive` instead),
   * so no re-enable is needed. Called when wall focus tears down
   * (Esc/Enter/floor change).
   */
  private exitDrawPhase(): void {
    if (!this.innerLineToolActive) return;
    this.innerLineToolActive = false;
    if (this.wallFocusEscListener) {
      window.removeEventListener('keydown', this.wallFocusEscListener);
      this.wallFocusEscListener = null;
    }
    // Deactivate the subtool (idempotent) so any stale polygon/region/brush
    // visuals from a previous activation are wiped on every wall-focus /
    // tool-disable / view-exit teardown path that routes through here, then
    // clear its undo stack so pre-commit/edit snapshots don't linger after
    // phase-2 ends (the outer door/window stack owns create/delete/kind undo).
    this.clearDrawSubToolUndo();
    this.innerHost?.deactivate();
    // Re-show phase-1 gizmos and refresh against the (possibly edited)
    // polygon. If the user committed an edit in phase-2 the opening's
    // geometry changed under us — refresh() recomputes frame + neighbors
    // from the current annotation state.
    this.openingGizmos.setVisible(true);
    this.openingGizmos.refresh();
  }

  protected onFloorChanged(_floor: DetectedFloorPlane | null, _ceiling: DetectedFloorPlane | null, groupIdentifier: string | null): void {
    // `FloorplanToolBase.handleSelectionChanged` fires this on every
    // `objects_selection_changed` event — including selection changes within
    // the same floor (e.g. clicking an opening's row in the sidebar). When
    // the floor group didn't actually change, the wall-focus / selection
    // teardown below would yank the user out of phase-2 every time they
    // selected an opening. Bail early when the group is unchanged; the
    // base class has already refreshed `currentFloorGroupIdentifier`
    // before calling us, so the comparison is against our last-seen value.
    if (groupIdentifier === this.lastFloorGroupIdentifier) return;
    this.lastFloorGroupIdentifier = groupIdentifier;

    // Floor changed — selection is no longer meaningful. Detection only runs
    // on explicit user action (wall click), so no auto-trigger here.
    this.clearSelection();
    this.clearWidgetWallFocus();
    this.clearMainWallView();
    this.hoveredRoomPick = null;
    this.hideRoomEdgeOverlay();
    // The previous skeleton is gone — re-subscribe to the new one.
    this.subscribeToWallSketchSelection();
  }

  /**
   * Resolve the rooms on the active floor. Reads live from `viewer.scene.rooms`
   * every call — that array is mutated in place when rooms are created /
   * deleted, so new rooms appear without the tool needing to re-subscribe.
   * A room belongs to the active floor iff its `groupIdentifier` matches the
   * selected floor group. The identifier list is used as a fallback for the
   * brief window before a freshly-created room has had its group assigned.
   */
  private getRoomsOnCurrentFloor(): PointoramaRoom[] {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId) return [];
    const idSet = this.currentFloorRoomIdentifiers.length > 0 ? new Set(this.currentFloorRoomIdentifiers) : null;
    return this.viewer.scene.rooms.filter(
      (room) => room.groupIdentifier === groupId || (idSet?.has(room.identifier) ?? false),
    );
  }

  /**
   * Outlines on the active floor. Same floor scoping as rooms — an outline
   * belongs to the floor whose group its `groupIdentifier` matches. Their
   * edges feed the wall picker alongside room edges so openings can be placed
   * on the building outline just like on a room wall.
   */
  private getOutlinesOnCurrentFloor(): PointoramaOutline[] {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId) return [];
    return (this.viewer.scene.outlines as PointoramaOutline[]).filter((o) => o.groupIdentifier === groupId);
  }

  // ─── Selection ──────────────────────────────────────────────────────
  // Edge selection STATE is owned by `edgeSelection`; its cyan line/node
  // visuals are suppressed while this tool drives it — this tool's wall
  // visual is the translucent strip overlay (see applyHoverSelection), and
  // the centerline highlight would otherwise linger across the phase-2 wall
  // view. Annotation selection is a plain id set this tool manages itself —
  // annotations have their own scene-side rendering, so no overlay needed.

  /** Read-only view of selected edge ids. */
  private get selectedEdgeIds(): ReadonlySet<string> {
    return this.edgeSelection?.getSelectedEdgeIds() ?? new Set();
  }

  /** Resolve the shared selection with this tool's visual suppression applied
   * — the selection lives on the (floor-dependent) sketch, so suppression is
   * (re)applied at use time rather than once at enable. */
  private suppressedEdgeSelection(): WallSketchSelection | null {
    const selection = this.edgeSelection;
    selection?.setVisualsSuppressed(true);
    return selection;
  }

  private setSelection(edgeIds: Iterable<string>, annotationIds: Iterable<string>): void {
    this.selectedAnnotationIds = new Set(annotationIds);
    // Set the skeleton edge selection last so its onSelectionChanged callback
    // observes the updated annotation set when it runs closeContextMenuIfEmpty.
    this.suppressedEdgeSelection()?.setSelection(null, edgeIds);
    this.syncOpeningGizmos();
  }

  private addToSelection(edgeIds: Iterable<string>, annotationIds: Iterable<string>): void {
    for (const id of annotationIds) this.selectedAnnotationIds.add(id);
    this.suppressedEdgeSelection()?.addToSelection([], edgeIds);
    this.syncOpeningGizmos();
  }

  private removeFromSelection(edgeIds: Iterable<string>, annotationIds: Iterable<string>): void {
    for (const id of annotationIds) this.selectedAnnotationIds.delete(id);
    this.suppressedEdgeSelection()?.removeFromSelection([], edgeIds);
    // The annotation set was just shrunk too; the edge-set callback may have
    // already closed the menu, but call again in case only annotations changed.
    this.closeContextMenuIfEmpty();
    this.syncOpeningGizmos();
  }

  private clearSelection(): void {
    this.selectedAnnotationIds.clear();
    this.edgeSelection?.setSelection(null, null);
    this.contextMenu?.hide();
    this.syncOpeningGizmos();
  }

  /**
   * Drive the OpeningGizmoController's lifecycle from the current
   * selection state. Attaches when EXACTLY one annotation is selected
   * and no edges (clean single-opening selection), detaches otherwise.
   * The controller is idempotent on `attach(sameId)` / `detach()`, so
   * this is safe to call after any selection mutation.
   *
   * Phase-2 (`innerLineToolActive`) doesn't detach — phase-2 enter/exit
   * use `setVisible(false/true)` instead, so the selection state
   * survives a round-trip through wall focus.
   */
  private syncOpeningGizmos(): void {
    const onlyOneAnnotation = this.selectedAnnotationIds.size === 1 && this.selectedEdgeIds.size === 0;
    if (!onlyOneAnnotation) {
      this.openingGizmos.detach();
      return;
    }
    const id = this.selectedAnnotationIds.values().next().value as string;
    const annotation = this.viewer.scene.measurements.find((m) => m instanceof OpeningAnnotation && m.identifier === id) as OpeningAnnotation | undefined;
    if (!annotation) {
      this.openingGizmos.detach();
      return;
    }
    this.openingGizmos.attach(annotation);
  }

  private closeContextMenuIfEmpty(): void {
    if (this.selectedEdgeIds.size === 0 && this.selectedAnnotationIds.size === 0) {
      this.contextMenu?.hide();
    }
  }

  /**
   * Walk a collinear chain through `seedEdgeId`, returning every edge id on
   * the chain plus every node id that bounds those edges (including the
   * in-between junction nodes the chain passes through). The chain extends
   * in both directions; at each junction we look for a single neighbouring
   * incident edge whose outgoing direction (away from the junction) lines
   * up with the chain's forward direction through that junction
   * (dot > 0.985, ≈ 10° tolerance — mirror of the skeleton editor's
   * anti-parallel pair test, which compares two outgoings sharing the same
   * source node).
   *
   * If multiple neighbours qualify, the straightest one wins. That's normally
   * fine; truly overlapping walls would tie at dot ≈ 1 and the first scanned
   * wins — acceptable since the chain still selects a valid wall strip.
   *
   * Returns null when the seed edge isn't on the active skeleton.
   */
  private findCollinearChain(skeleton: PointoramaWallSketch, seedEdgeId: string): { edgeIds: string[]; nodeIds: string[] } | null {
    const seed = skeleton.getEdge(seedEdgeId);
    if (!seed) return null;

    const COLLINEAR_DOT_THRESHOLD = 0.985;

    /**
     * Unit direction of `edge` pointing AWAY from `fromNodeId`. Returns null
     * when the edge has zero length or unresolvable nodes.
     */
    const outgoingDir = (edge: { startNodeId: string; endNodeId: string }, fromNodeId: string): { x: number; y: number } | null => {
      const fromNode = skeleton.getNode(fromNodeId);
      const toNodeId = edge.startNodeId === fromNodeId ? edge.endNodeId : edge.startNodeId;
      const toNode = skeleton.getNode(toNodeId);
      if (!fromNode || !toNode) return null;
      const dx = toNode.position.x - fromNode.position.x;
      const dy = toNode.position.y - fromNode.position.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) return null;
      return { x: dx / len, y: dy / len };
    };

    const visited = new Set<string>([seed.id]);
    const chainEdges: string[] = [seed.id];

    /**
     * Walk away from `currentEdge` past `currentNodeId`, extending the chain
     * by collinear neighbours until none exists. `prepend` controls which
     * end of `chainEdges` new edges land on so the final list reads in
     * spatial order along the wall.
     */
    const walk = (startEdge: { id: string; startNodeId: string; endNodeId: string }, startNodeId: string, prepend: boolean): void => {
      let currentEdge = startEdge;
      let currentNodeId = startNodeId;
      while (true) {
        const node = skeleton.getNode(currentNodeId);
        if (!node) return;
        // Direction the chain is pointing as it passes through this junction:
        // outgoing from the OTHER end of the current edge, toward this node.
        // Equivalent to negating the current edge's outgoing direction from
        // currentNodeId.
        const outFromHere = outgoingDir(currentEdge, currentNodeId);
        if (!outFromHere) return;
        const chainInto = { x: -outFromHere.x, y: -outFromHere.y };

        // The continuation edge must leave this node in the same physical
        // direction the chain is travelling — its outgoing direction (away
        // from currentNodeId) must be parallel to chainInto. At T/cross
        // junctions, perpendicular stubs land at dot ≈ 0 and are filtered
        // out; only collinear neighbours pass the threshold.
        let bestNextEdgeId: string | null = null;
        let bestDot = COLLINEAR_DOT_THRESHOLD;
        for (const candEdgeId of node.edgeIds) {
          if (visited.has(candEdgeId)) continue;
          const candEdge = skeleton.getEdge(candEdgeId);
          if (!candEdge) continue;
          const candOut = outgoingDir(candEdge, currentNodeId);
          if (!candOut) continue;
          const dot = chainInto.x * candOut.x + chainInto.y * candOut.y;
          if (dot > bestDot) {
            bestDot = dot;
            bestNextEdgeId = candEdgeId;
          }
        }
        if (!bestNextEdgeId) return;
        const nextEdge = skeleton.getEdge(bestNextEdgeId);
        if (!nextEdge) return;
        visited.add(bestNextEdgeId);
        if (prepend) chainEdges.unshift(bestNextEdgeId);
        else chainEdges.push(bestNextEdgeId);
        currentNodeId = nextEdge.startNodeId === currentNodeId ? nextEdge.endNodeId : nextEdge.startNodeId;
        currentEdge = nextEdge;
      }
    };

    walk(seed, seed.startNodeId, /* prepend */ true);
    walk(seed, seed.endNodeId, /* prepend */ false);

    // Collect every node id that's an endpoint of any chain edge.
    const nodeIdSet = new Set<string>();
    for (const edgeId of chainEdges) {
      const e = skeleton.getEdge(edgeId);
      if (!e) continue;
      nodeIdSet.add(e.startNodeId);
      nodeIdSet.add(e.endNodeId);
    }

    return { edgeIds: chainEdges, nodeIds: Array.from(nodeIdSet) };
  }

  // ─── Hover ──────────────────────────────────────────────────────────

  /**
   * Pixel-space hover. Annotations are tested first because they sit visually
   * "on top" of the skeleton — the user expects to grab a placed door before
   * the underlying edge.
   */
  private updateHover(event: MouseEvent): void {
    const mousePx = this.hitTester.mousePx(event);

    // 1) Door/window AreaAnnotations. Two complementary hit tests:
    //   - Point-in-polygon for views where the rectangle has visible area
    //     (3D perspective, wall-focus).
    //   - Segment distance for the top-down X-Ray view, where the four
    //     wall-rectangle corners (p0/p3 bottom-left/top-left,
    //     p1/p2 bottom-right/top-right) collapse to a near-zero-thickness
    //     line in screen space and the polygon test always misses. We
    //     test the cursor against the bottom edge (p0 → p1, which is also
    //     coincident with the top edge p3 → p2 from above) within the
    //     same pixel radius the wall-edge hover uses.
    let bestAnnotationId: string | null = null;
    for (const m of this.viewer.scene.measurements) {
      if (!(m instanceof OpeningAnnotation)) continue;
      if (m.points.length < 3) continue;
      const projected: THREE.Vector2[] = [];
      let allOnScreen = true;
      for (const p of m.points) {
        const px = this.hitTester.projectWorldToPixel(p.position);
        if (!px) {
          allOnScreen = false;
          break;
        }
        projected.push(px);
      }
      if (!allOnScreen) continue;
      if (PixelHitTester.isPointInPolygon2D(mousePx, projected)) {
        bestAnnotationId = m.identifier;
        break;
      }
      // Degenerate-projection fallback: in top-down X-Ray the wall rectangle
      // collapses to a near-zero-thickness line and the polygon test
      // misses. Test all four edges of the projected rectangle and accept
      // the cursor if it's within the hover radius of any of them. Using
      // every edge (not just the bottom one) handles both point ordering
      // variations across creation paths AND the perspective-view case
      // where the top edge can be closer to the cursor than the bottom.
      let minEdgeDist = Infinity;
      for (let i = 0; i < projected.length; i++) {
        const a = projected[i];
        const b = projected[(i + 1) % projected.length];
        const d = PixelHitTester.pointToSegmentDistancePx(mousePx, a, b);
        if (d < minEdgeDist) minEdgeDist = d;
      }
      if (minEdgeDist <= HOVER_EDGE_RADIUS_PX) {
        bestAnnotationId = m.identifier;
        break;
      }
    }

    if (bestAnnotationId) {
      this.hoveredAnnotationId = bestAnnotationId;
      this.hoveredEdgeId = null;
      this.hoveredRoomPick = null;
      return;
    }

    // 1b) Door/window GLYPH symbols (leaf + swing arc / glass lines). These
    //     extend past the opening's drawn rectangle (e.g. the swing arc reaches
    //     into the room), so a click there misses the polygon test above. Hit-
    //     test the glyph polylines so clicking the symbol selects its opening.
    const skeletonForGlyph = this.getCurrentFloorWallSketch();
    if (skeletonForGlyph) {
      const glyphPick = skeletonForGlyph.pickOpeningGlyphAtPixel(mousePx, this.hitTester, HOVER_EDGE_RADIUS_PX);
      if (glyphPick) {
        this.hoveredAnnotationId = glyphPick.openingId;
        this.hoveredEdgeId = null;
        this.hoveredRoomPick = null;
        return;
      }
    }

    // 2) WallSketch edges — delegate to the skeleton's shared picker so this
    //    tool gets the same wide/smart hover the WallSketchEditor uses
    //    (rendered-quad polygon test + centerline-strip fallback +
    //    midpoint-ranking when walls overlap).
    const skeleton = this.getCurrentFloorWallSketch();
    if (skeleton) {
      const pick = skeleton.pickEdgeAtPixel(mousePx, this.hitTester, { centerlineRadiusPx: HOVER_EDGE_RADIUS_PX, excludeOutline: true });
      if (pick) {
        this.hoveredAnnotationId = null;
        this.hoveredEdgeId = pick.edgeId;
        this.hoveredRoomPick = null;
        return;
      }
    }

    // 3) Room/outline-edge fallback — pick the closest room-polygon edge and
    //    perpendicular-ray across to the opposite room's edge. See
    //    `RoomEdgeFallback` for the algorithm and the "first hit same room
    //    → abort" rule. This runs whenever NO sketch edge is under the
    //    cursor — including on floors that DO have a sketch: a click then
    //    materializes a loose sketch edge between the room/outline edges
    //    (see focusWallFromRoomPick), so walls missing from the sketch are
    //    still workable.
    this.hoveredAnnotationId = null;
    this.hoveredEdgeId = null;
    this.hoveredRoomPick = this.computeRoomEdgePick(event, mousePx);
  }

  /**
   * Run the no-skeleton room-edge picker for the cursor at `event`. Pulled
   * into its own method so click handlers can call it directly (a click
   * without a preceding mousemove on touch / synthesised events would
   * otherwise see stale or null state).
   *
   * Returns null on any of: no rooms on the floor, no floor Z resolvable,
   * cursor not on a room edge within the pixel radius, or the perpendicular
   * ray's first hit was on the same room (the "abort" rule).
   */
  private computeRoomEdgePick(event: MouseEvent, mousePx: THREE.Vector2): RoomEdgePick | null {
    // The room/outline fallback (imagined walls, materialization) is a
    // MEASURED-source affordance: in the standardised (wall-sketch) mode the
    // sketch is authoritative — missing walls are drawn in the wall editor,
    // never imagined from rooms.
    if (!(this.viewer.floorplanRepresentation?.isOriginalActive(this.currentFloorGroupIdentifier) ?? false)) return null;
    const rooms = this.getRoomsOnCurrentFloor();
    const outlines = this.getOutlinesOnCurrentFloor();
    if (rooms.length === 0 && outlines.length === 0) return null;
    const floorZ = this.resolveFloorZ();
    if (floorZ === null) return null;
    const cursorWorld = this.cursorWorldAnchor(event);
    if (!cursorWorld) return null;
    // Outline edges are wall candidates just like room edges.
    const edges = [...collectRoomEdges(rooms), ...collectOutlineEdges(outlines)];
    if (edges.length === 0) return null;
    // Outer rings double as the hard outer boundary: when present, the picker
    // rejects any cursor outside them (no selection beyond the outline, and the
    // outline edge itself is pickable only from its inside).
    const outlineRings = outlines.map((o) => o.getOuterRing());
    return pickRoomEdgeAtPixel(edges, rooms, mousePx, cursorWorld, this.hitTester, floorZ, outlineRings);
  }

  public onMouseMove(event: MouseEvent): void {
    // Phase-2: the inner LineTool owns input on the wall view. Forward the
    // move into the host and skip phase-1 hover entirely — matches the
    // pre-refactor behavior where the inner tool's own DOM listener got the
    // move and phase-1 hover was gated off.
    if (this.innerLineToolActive) {
      this.innerHost?.setHostDragging(this.isDragging);
      this.innerHost?.routeMouseMove(event);
      return;
    }
    // Active gizmo drag takes precedence — forward and bail before hover
    // logic runs so the cursor doesn't jitter between pointer/crosshair
    // and so the wall-strip cyan highlight doesn't reappear mid-drag.
    if (this.openingGizmos.isDragging) {
      this.openingGizmos.onMouseMove(event);
      this.viewer.renderer.domElement.style.cursor = 'grabbing';
      return;
    }
    // An opening-sketch slide in flight owns the interaction — no hover
    // updates (they'd fight the drag's target) and a grabbing cursor.
    if (this.openingSlide?.moved) {
      this.viewer.renderer.domElement.style.cursor = 'grabbing';
      return;
    }
    // Cursor sitting on a gizmo handle gets `grab` (drag affordance) and
    // suppresses the wall-strip cyan highlight + annotation hover — the
    // gizmos belong to a SELECTED opening, and the user doesn't want the
    // background wall hover competing for visual attention.
    if (this.openingGizmos.isHoveredOverGizmo(event)) {
      this.viewer.renderer.domElement.style.cursor = 'grab';
      // Hide any latent wall-strip hover overlay so it doesn't linger under
      // the gizmo (the click selection itself is left untouched — hover no
      // longer drives selection). Skip `updateHover` entirely so the next
      // mousemove still has fresh state if the cursor leaves the gizmo.
      this.hideRoomEdgeOverlay();
      return;
    }
    this.updateHover(event);
    // Pointer over anything clickable — a door/window annotation, a wall
    // edge, or the no-skeleton room-edge pick (a click on any of these
    // selects / runs detection). Crosshair only over dead space. Annotations
    // win the hit test, so this also reads correctly when the cursor sits on
    // both an annotation and the wall beneath.
    this.viewer.renderer.domElement.style.cursor = this.flipInteraction.isOverHandle(event) || this.hoveredAnnotationId || this.hoveredEdgeId || this.hoveredRoomPick ? 'pointer' : 'crosshair';
    // Phase-1 visuals (cyan wall-strip overlay on hover, widget wall
    // preview) belong to the floor view. Once the main view is in wall
    // focus we're in phase-2 idle — the only hover that matters is the
    // door/window annotation under the cursor, which `updateHover`
    // already tracks. Same while a wall is click-SELECTED (the widget shows
    // its 3D view): hovering other walls must neither highlight them nor
    // stomp the selected wall's own highlight — the selection stands until
    // the user clicks elsewhere.
    if (this.isWallFocusActive() || this.widgetClickFocusKey !== null) return;
    this.applyHoverSelection();
    this.updateWidgetWallHover();
  }

  /**
   * Selection follows hover. When the cursor sits on a skeleton edge, the
   * full collinear chain (every connected edge whose direction is collinear
   * within ~10°, plus every node bounding those edges) is selected so the
   * cyan overlay highlights the entire wall strip — not just the one
   * segment the cursor happens to be on. Annotations win the hit test, so
   * an annotation hover clears the edge selection. Empty hover also clears.
   *
   * Replaces click-driven selection in this tool: the user's intent is
   * "show me what wall I'm pointing at," not "lock this in." The menu /
   * place-door workflows still receive the resulting selection but become
   * inert because the selection clears as soon as the cursor leaves.
   */
  private applyHoverSelection(): void {
    // Sketch-edge hover: render the hovered collinear chain as the same
    // translucent wall-strip box the room-edge fallback uses — NOT the
    // skeleton's cyan centerline highlight (this tool suppresses those
    // selection visuals entirely; see ensureSelectionVisualsSuppressed).
    const skeleton = this.getCurrentFloorWallSketch();
    if (skeleton && this.hoveredEdgeId) {
      const chain = this.findCollinearChain(skeleton, this.hoveredEdgeId);
      const box = chain ? this.buildWallStripBox(skeleton, chain.edgeIds) : null;
      if (box) {
        this.ensureRoomEdgeOverlay().showBox(box);
        return;
      }
    }
    // Room-edge fallback (works on sketchless floors AND in the wall gaps of
    // sketch floors): render the wall-box overlay between the two room edges
    // identified by the perpendicular ray.
    if (this.hoveredRoomPick) {
      this.ensureRoomEdgeOverlay().show(this.hoveredRoomPick);
      return;
    }
    this.hideRoomEdgeOverlay();
  }

  /**
   * 4-corner band of a hovered wall chain: the chain's extent along its
   * direction × the MINIMUM left/right widths across its edges (a chain can
   * hold several edges with different thicknesses — the strip shows the
   * guaranteed-wall core). Left/right are stored relative to each edge's own
   * start→end direction, so edges pointing against the chain direction swap
   * sides before taking the minimum. Curved edges contribute their chord.
   */
  private buildWallStripBox(skeleton: PointoramaWallSketch, chainEdgeIds: string[]): THREE.Vector3[] | null {
    const z = skeleton.getFloorHeight();
    let dir: THREE.Vector2 | null = null;
    const pts: THREE.Vector2[] = [];
    let minLeft = Infinity;
    let minRight = Infinity;
    for (const edgeId of chainEdgeIds) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) continue;
      const a = skeleton.getNode(edge.startNodeId);
      const b = skeleton.getNode(edge.endNodeId);
      if (!a || !b) continue;
      const pa = new THREE.Vector2(a.position.x, a.position.y);
      const pb = new THREE.Vector2(b.position.x, b.position.y);
      const edgeDir = pb.clone().sub(pa);
      if (edgeDir.lengthSq() < 1e-12) continue;
      edgeDir.normalize();
      if (!dir) dir = edgeDir.clone();
      pts.push(pa, pb);
      const sameWay = edgeDir.dot(dir) >= 0;
      const left = edge.leftSize ?? 0.1;
      const right = edge.rightSize ?? 0.1;
      minLeft = Math.min(minLeft, sameWay ? left : right);
      minRight = Math.min(minRight, sameWay ? right : left);
    }
    if (!dir || pts.length < 2) return null;
    if (!Number.isFinite(minLeft)) minLeft = 0.1;
    if (!Number.isFinite(minRight)) minRight = 0.1;

    // Chain extent along its direction.
    const base = pts[0];
    let minT = Infinity;
    let maxT = -Infinity;
    for (const p of pts) {
      const t = p.clone().sub(base).dot(dir);
      if (t < minT) minT = t;
      if (t > maxT) maxT = t;
    }
    const start = base.clone().add(dir.clone().multiplyScalar(minT));
    const end = base.clone().add(dir.clone().multiplyScalar(maxT));
    // Same left convention as the skinning (arcGeometry's leftPerp): +90°.
    const leftN = new THREE.Vector2(-dir.y, dir.x);
    const corner = (p: THREE.Vector2, n: THREE.Vector2, d: number): THREE.Vector3 => new THREE.Vector3(p.x + n.x * d, p.y + n.y * d, z);
    return [corner(start, leftN, minLeft), corner(end, leftN, minLeft), corner(end, leftN, -minRight), corner(start, leftN, -minRight)];
  }

  private ensureRoomEdgeOverlay(): RoomEdgeHoverOverlay {
    if (!this.roomEdgeOverlay) {
      this.roomEdgeOverlay = new RoomEdgeHoverOverlay(this.viewer);
    }
    return this.roomEdgeOverlay;
  }

  private hideRoomEdgeOverlay(): void {
    this.roomEdgeOverlay?.hide();
  }

  // ─── Input ──────────────────────────────────────────────────────────
  // All selection input — click, mousedown snapshot, and box-select drag —
  // is gated to the **left mouse button only**. Middle-click is reserved
  // for camera pan and right-click for the platform-default behavior; both
  // pass through the tool untouched. Input is handled in both the 2D ortho
  // floor view and the 3D perspective view: hover + the room-edge wall pick
  // and `cursorWorldAnchor` all work against either camera, so a 3D click
  // can focus a wall just like a 2D one. Branches that are genuinely
  // floor-view-only (skeleton multi-select, box-select) simply find no
  // skeleton/state in 3D and no-op.

  /** Snapshot the hover target at mousedown time so a drag-threshold cursor drift doesn't lose it. */
  public onMouseDown(event: MouseEvent): void {
    // Phase-2: the inner LineTool owns input on the wall view. Forward the
    // event into the host (the subtool no longer binds its own DOM
    // listeners) — no phase-1 hover snapshot, no click target. Forward
    // BEFORE the left-button gate so the inner mode also receives
    // right-button mousedown (used to start the prompt-delete drag box)
    // and middle-button (camera pan passes through the inner mode).
    if (this.innerLineToolActive) {
      this.innerHost?.setHostDragging(this.isDragging);
      this.innerHost?.routeMouseDown(event);
      return;
    }
    if (event.button !== 0) return;
    // A mousedown on a flip gizmo arms the flip and consumes the interaction.
    if (this.flipInteraction.handleMouseDown(event)) return;
    // Give the gizmo controller first dibs on the mousedown. A handle hit
    // starts a drag and short-circuits the selection-snapshot path —
    // without this the user's mousedown on a translate sphere would also
    // trigger annotation re-selection through `updateHover`, which would
    // detach the gizmo mid-drag.
    if (this.openingGizmos.isAttached()) {
      const hit = this.openingGizmos.pickAt(event);
      if (hit) {
        this.openingGizmos.beginDrag(hit, event);
        event.stopPropagation();
        return;
      }
    }
    this.updateHover(event);
    this.clickTargetEdgeId = this.hoveredEdgeId;
    this.clickTargetAnnotationId = this.hoveredAnnotationId;

    // A hovered OPENING_SKETCH arms a slide along its wall — dragging the
    // door/window body moves it, like in the sketch-object tool. Legacy
    // OpeningAnnotation polygons keep their gizmo-only editing (they're free
    // polygons, not parametric wall offsets).
    this.openingSlide = null;
    const hoveredId = this.hoveredAnnotationId;
    if (hoveredId && this.viewer.scene.openingSketches.some((o) => o.identifier === hoveredId)) {
      const skeleton = this.getCurrentFloorWallSketch();
      if (skeleton) {
        try {
          const edgeId = getOpeningSketchWallEdgeIdProperty(hoveredId).value || '';
          const beforeOffset = getOpeningSketchOffsetProperty(hoveredId).value ?? 0;
          const cursor = edgeId ? cursorOffsetOnOpeningEdge(event, this.viewer, skeleton, edgeId) : null;
          if (edgeId && cursor !== null) {
            this.openingSlide = { openingId: hoveredId, edgeId, grabDelta: cursor - beforeOffset, beforeOffset, moved: false };
          }
        } catch {
          /* property service not ready */
        }
      }
    }
  }

  /** End an active gizmo drag. BaseTool fires `onMouseUp` after a drag completes. */
  public onMouseUp(event: MouseEvent): void {
    // Phase-2: forward the mouseup into the inner host so the subtool can
    // close a drag / prompt interaction. The subtool no longer has its own
    // DOM listener. Forward BEFORE the left-button gate so right-button
    // mouseup also reaches the inner mode (used to finalise the prompt
    // delete-box drag).
    if (this.innerLineToolActive) {
      this.innerHost?.setHostDragging(this.isDragging);
      this.innerHost?.routeMouseUp(event);
      return;
    }
    if (event.button !== 0) return;
    if (this.openingGizmos.isDragging) {
      this.openingGizmos.endDrag();
      event.stopPropagation();
    }
  }

  public onClick(event: MouseEvent): void {
    if (event.button !== 0) return;
    // Phase-2: forward the click into the inner host (e.g. place a line
    // prompt / enter edit on a clicked opening). The subtool has no DOM
    // listener of its own.
    if (this.innerLineToolActive) {
      this.innerHost?.routeClick(event);
      return;
    }
    if (this.flipInteraction.handleClick()) return;
    // If the gizmo controller just consumed a mousedown (handle pick or
    // an in-flight drag that finished), the synthetic click that follows
    // should NOT re-evaluate background selection. Clear the flag so the
    // next interaction starts fresh.
    if (this.openingGizmos.consumedThisInteraction) {
      this.openingGizmos.clearConsumedFlag();
      return;
    }
    // Trailing click of an opening-slide drag — the drop point must not
    // re-run background selection (cleared via setTimeout in onDragEnd).
    if (this.openingSlide?.moved) return;

    // Refresh hover at the click pixel — covers the case of a click without a
    // preceding mousemove (cursor enters the canvas mid-click, etc.).
    this.updateHover(event);

    try {
      // No-skeleton fallback: a plain click on a hovered room-edge wall
      // routes through the same wall-focus flow the skeleton path uses.
      // Modifier-held clicks are intentionally ignored here — multi-select
      // / box-select state is skeleton-edge-only, so there's no meaningful
      // "add this fallback wall to the selection set" semantic.
      if (this.hoveredRoomPick && !this.hoveredAnnotationId && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        void this.focusWallFromRoomPick(this.hoveredRoomPick);
        return;
      }

      // Empty-space click without modifiers → clear selection.
      if (!this.hoveredEdgeId && !this.hoveredAnnotationId) {
        if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
          this.setSelection([], []);
        }
        return;
      }

      // Click on an edge selects the entire collinear wall strip (same chain
      // logic the hover overlay uses), not just the single segment under the
      // cursor. Annotations stay single-target — they already wrap a whole
      // opening.
      let edgeIds: string[] = [];
      if (this.hoveredEdgeId) {
        const skeleton = this.getCurrentFloorWallSketch();
        const chain = skeleton ? this.findCollinearChain(skeleton, this.hoveredEdgeId) : null;
        edgeIds = chain ? chain.edgeIds : [this.hoveredEdgeId];
      }
      const annotationIds = this.hoveredAnnotationId ? [this.hoveredAnnotationId] : [];
      // Shift is a full multi-select modifier, same as ctrl/meta — the select
      // tool treats all three identically (toggle in/out of the selection).
      const additive = event.ctrlKey || event.metaKey || event.shiftKey;

      if (additive) {
        const inSelection = (this.hoveredEdgeId && this.selectedEdgeIds.has(this.hoveredEdgeId)) || (this.hoveredAnnotationId && this.selectedAnnotationIds.has(this.hoveredAnnotationId));
        if (inSelection) {
          this.removeFromSelection(edgeIds, annotationIds);
        } else {
          this.addToSelection(edgeIds, annotationIds);
        }
      } else {
        this.setSelection(edgeIds, annotationIds);
      }

      const hasSelection = this.selectedEdgeIds.size > 0 || this.selectedAnnotationIds.size > 0;
      if (hasSelection) {
        const anchor = this.cursorWorldAnchor(event);
        if (anchor) this.showContextMenuForSelection(anchor);
      } else {
        this.contextMenu?.hide();
      }

      // Mirror the select-tool's selection on the clicked annotation so the
      // sidebar / global selection state pick it up. The internal
      // `selectedAnnotationIds` set above drives this tool's context menu;
      // dispatching here makes the annotation also "selected" in the
      // standard Redux-backed sense, like clicking it under the select tool.
      if (this.hoveredAnnotationId) {
        const annotation = this.viewer.scene.measurements.find((m) => m instanceof OpeningAnnotation && m.identifier === this.hoveredAnnotationId);
        if (annotation) {
          this.viewer.scene.dispatchEvent({
            type: 'measurement_select',
            value: { measure: annotation, isCtrl: additive },
          });
        } else {
          // Glyph ids can also belong to OPENING_SKETCHes (the wall sketch
          // draws its openings' door/window symbols) — new openings are
          // sketches, so resolve those too and select them the standard way.
          const openingSketch = this.viewer.scene.openingSketches.find((o) => o.identifier === this.hoveredAnnotationId);
          if (openingSketch) {
            this.viewer.scene.dispatchEvent({
              type: 'measurement_select',
              value: { measure: openingSketch, isCtrl: additive },
            });
          }
        }
      }

      // Persistent widget focus on the single selected edge. Multi-edge or
      // annotation-only selections clear the focus — there's no "frame all"
      // mode for the widget here. Hover preview is wiped in either case.
      this.applyWidgetWallClick();

      // Phase-1 auto-flow: a plain click on a wall edge (no annotation under
      // cursor, no modifier keys) auto-runs detection on the wall strip and
      // installs a wall-focus override on the main view (perpendicular ortho
      // + clip box, same as the widget's focusOnWall). Modifier-held clicks
      // keep the legacy add-to-selection behaviour so power users can still
      // build up multi-strip selections + use the context menu.
      const plainEdgeClick = this.hoveredEdgeId && !this.hoveredAnnotationId && !additive && edgeIds.length > 0;
      if (plainEdgeClick) {
        void this.focusWallFromEdges(edgeIds);
      }
    } finally {
      this.clickTargetEdgeId = null;
      this.clickTargetAnnotationId = null;
    }
  }

  // Box-select drag is intentionally not engaged. The old multi-wall
  // context-menu UX (Shift/Ctrl box-select → place door/window) has been
  // replaced by single-click → auto-detect-and-focus. Engaging box-select
  // on a regular click with sub-threshold mouse motion was suppressing
  // the click handler and the user saw a phantom rectangle.
  public onDragStart(data: DragEventData): void {
    if (this.flipInteraction.handleDragStart()) return;
    // Phase-2: forward to the inner draw subtool so it can grab + drag an
    // existing opening's vertex/edge (MeasureSubTool editing). Phase-1 has no
    // drag interaction of its own.
    if (this.innerLineToolActive) {
      this.innerHost?.setHostDragging(this.isDragging);
      this.innerHost?.routeDragStart(data);
    }
  }

  public onDrag(data: DragEventData): void {
    if (this.innerLineToolActive) {
      this.innerHost?.setHostDragging(this.isDragging);
      this.innerHost?.routeDrag(data);
      return;
    }
    if (data.button !== 0) return;
    // A gizmo-handle drag moves the opening along the handle's axis (offset /
    // inset) — owned entirely by the flip interaction.
    if (this.flipInteraction.handleDrag(data.event)) return;
    // Slide an existing opening sketch along its wall (clamped to the edge) —
    // same live flow as the sketch-object tool: offset writes patch the glyph,
    // the wall hole is re-cut rAF-throttled.
    if (this.openingSlide) {
      const skeleton = this.getCurrentFloorWallSketch();
      if (!skeleton) return;
      const slide = this.openingSlide;
      const cursor = cursorOffsetOnOpeningEdge(data.event, this.viewer, skeleton, slide.edgeId);
      if (cursor === null) return;
      let length = 0;
      try {
        length = getOpeningSketchLengthProperty(slide.openingId).value ?? 0;
      } catch {
        return;
      }
      const clamped = clampOpeningOffsetToClearSpan(skeleton, slide.edgeId, length, cursor - slide.grabDelta);
      try {
        getOpeningSketchOffsetProperty(slide.openingId).value = clamped;
      } catch {
        return;
      }
      slide.moved = true;
      this.holeRebuild.schedule();
    }
  }

  public onDragEnd(data: DragEventData): void {
    if (this.innerLineToolActive) {
      this.innerHost?.routeDragEnd(data);
      this.innerHost?.setHostDragging(false);
      return;
    }
    // Finish a gizmo-handle drag: commit + undo handled by the wrapper.
    if (this.flipInteraction.handleDragEnd()) return;
    // Finish an opening slide: undo snapshot + final authoritative hole
    // re-cut + re-file into the adjacent room's folder.
    if (this.openingSlide) {
      this.holeRebuild.cancel();
      const slide = this.openingSlide;
      if (slide.moved) {
        let afterOffset = slide.beforeOffset;
        try {
          afterOffset = getOpeningSketchOffsetProperty(slide.openingId).value ?? slide.beforeOffset;
        } catch {
          /* property service not ready */
        }
        if (Math.abs(afterOffset - slide.beforeOffset) > 1e-6) {
          this.undoRedo?.pushState(createOpeningOffsetMoveSnapshot(() => this.getCurrentFloorWallSketch(), slide.openingId, slide.beforeOffset, afterOffset));
          this.getCurrentFloorWallSketch()?.updateSkinningVisualization();
          this.viewer.scene.dispatchEvent({ type: 'opening_sketch_moved', value: { identifier: slide.openingId } });
        }
        // Keep the slide state until the trailing click is swallowed in onClick.
        setTimeout(() => {
          this.openingSlide = null;
        }, 0);
      } else {
        this.openingSlide = null;
      }
    }
  }

  /** True iff the main view currently has the Wall focus card active. */
  private isWallFocusActive(): boolean {
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (!mainState) return false;
    return mainState.currentViewId.value === WALL_FOCUS_VIEW_ID;
  }

  /**
   * View to return to when wall focus exits — captured from the global
   * ViewManager at entry (see `applyWallFocusAndEnterDrawPhase`). Null when
   * unknown; callers fall back to X-Ray. Never the transient wall-focus id.
   */
  private wallFocusReturnViewId: string | null = null;

  /** The captured return view, ignoring the wall-focus id itself. */
  private wallFocusOriginViewId(): string | null {
    const id = this.wallFocusReturnViewId;
    if (!id || id === WALL_FOCUS_VIEW_ID) return null;
    return id;
  }

  public onKeyDown(event: KeyboardEvent): void {
    // Phase-2: the inner LineTool no longer has its own keydown listener, so
    // forward synchronously into the host. It gets first crack at Esc/Enter
    // and will preventDefault/stopPropagation when it consumes them (refining
    // a prompt, editing a shape). The window-level `wallFocusEscListener`
    // remains the fallback for the idle Esc/Enter wall-focus teardown — it
    // bails when the inner has prompts/an editing shape, or when the key was
    // already `defaultPrevented`.
    if (this.innerLineToolActive) {
      this.innerHost?.routeKeyDown(event);
      return;
    }
    if (event.key === 'Escape' || event.key === 'Enter') {
      // If a wall-focus card is active on the main view, return to the
      // top-down X-Ray floor view. Esc and Enter are both treated as
      // "done editing this wall" — Enter from the LineTool's prompt-edit
      // workflow, Esc as the universal cancel.
      const mainState = this.viewer.renderViews[0]?.viewState;
      if (mainState?.getCustomView(WALL_FOCUS_VIEW_ID)) {
        event.preventDefault();
        event.stopPropagation();
        // Atomic exit: `mainState.setView` restores the wall-focus tab's
        // origin overrides (clip/material/cameraClip), applies the
        // target view's declarative attrs, AND syncs the global
        // ViewManager so X-Ray's imperative density-flag hook fires.
        // `clearMainWallView` then tears down the tab entry, the inner
        // LineTool, and the `viewerMode.associatedPlane` swap. Return to the
        // origin view so a wall focused from 3D exits back to 3D.
        mainState.setView(this.wallFocusOriginViewId() ?? ViewModes.XRAY_2D.id);
        this.clearMainWallView();
        return;
      }
      if (event.key === 'Escape' && (this.selectedEdgeIds.size > 0 || this.selectedAnnotationIds.size > 0)) {
        event.preventDefault();
        event.stopPropagation();
        this.clearSelection();
      }
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.deleteSelection()) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  /**
   * Phase-2 only: forward key-up into the inner host. The inner LineTool's
   * onKeyUp drives Ctrl-release / plane-refinement state; in phase-1 the
   * outer tool has no key-up behavior so this is a no-op.
   */
  public onKeyUp(event: KeyboardEvent): void {
    if (this.innerLineToolActive) this.innerHost?.routeKeyUp(event);
  }

  /**
   * Phase-2 only: forward the wheel into the inner host. Phase-1 has no
   * wheel behavior (camera zoom passes through untouched).
   */
  public onWheel(event: WheelEvent): void {
    if (this.innerLineToolActive) this.innerHost?.routeWheel(event);
  }

  /**
   * Phase-2 only: forward right-click into the inner host (the LineTool
   * uses it to remove the last prompt). Phase-1 leaves right-click to the
   * platform default (camera / context).
   */
  public onRightClick(event: MouseEvent): void {
    if (this.innerLineToolActive) this.innerHost?.routeRightClick(event);
  }

  // ─── Box-select ─────────────────────────────────────────────────────

  private ensureBoxSelect(): BoxSelectController {
    if (!this.boxSelect) {
      this.boxSelect = new BoxSelectController(this.viewer, {
        boxStyle: {
          border: '1.5px dashed #00ddff',
          background: 'rgba(0, 221, 255, 0.08)',
        },
      });
    }
    return this.boxSelect;
  }

  private commitBoxSelect(mode: 'replace' | 'add' | 'remove', rect: { minX: number; maxX: number; minY: number; maxY: number }): void {
    const { minX, maxX, minY, maxY } = rect;

    // Edges in box. Each segment that intersects the rect drags in its full
    // collinear chain — selecting a wall strip means selecting the entire
    // strip even when only one of its sub-segments crosses the box.
    const hitEdgeIds: string[] = [];
    const skeleton = this.getCurrentFloorWallSketch();
    if (skeleton) {
      const expanded = new Set<string>();
      for (const edge of skeleton.getEdges()) {
        const a = skeleton.getNode(edge.startNodeId);
        const b = skeleton.getNode(edge.endNodeId);
        if (!a || !b) continue;
        const ap = this.hitTester.projectWorldToPixel(a.position);
        const bp = this.hitTester.projectWorldToPixel(b.position);
        if (!ap || !bp) continue;
        if (!PixelHitTester.segmentIntersectsRect(ap, bp, minX, minY, maxX, maxY)) continue;
        if (expanded.has(edge.id)) continue;
        const chain = this.findCollinearChain(skeleton, edge.id);
        const ids = chain ? chain.edgeIds : [edge.id];
        for (const id of ids) {
          if (expanded.has(id)) continue;
          expanded.add(id);
          hitEdgeIds.push(id);
        }
      }
    }

    // Annotations in box — count an annotation as "in" if any of its corners
    // is inside the rect (matches the user's intuition for small polygons).
    const hitAnnotationIds: string[] = [];
    for (const m of this.viewer.scene.measurements) {
      if (!isDoorWindowAnnotation(m)) continue;
      let inside = false;
      for (const p of m.points) {
        const px = this.hitTester.projectWorldToPixel(p.position);
        if (px && PixelHitTester.pointInRect(px, minX, minY, maxX, maxY)) {
          inside = true;
          break;
        }
      }
      if (inside) hitAnnotationIds.push(m.identifier);
    }

    if (mode === 'replace') {
      this.setSelection(hitEdgeIds, hitAnnotationIds);
    } else if (mode === 'add') {
      this.addToSelection(hitEdgeIds, hitAnnotationIds);
    } else {
      this.removeFromSelection(hitEdgeIds, hitAnnotationIds);
    }
  }

  // ─── Context menu ───────────────────────────────────────────────────

  private showContextMenuForSelection(worldAnchor: THREE.Vector3): void {
    if (this.selectedEdgeIds.size === 0 && this.selectedAnnotationIds.size === 0) return;
    if (!this.contextMenu) {
      this.contextMenu = new ContextMenu(this.viewer);
    }
    const menu = this.contextMenu;
    const items: ContextMenuItem[] = this.buildContextMenuItems();
    if (items.length === 0) {
      menu.hide();
      return;
    }
    menu.show(items, worldAnchor);
  }

  private buildContextMenuItems(): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    const annotationsOnly = this.selectedEdgeIds.size === 0 && this.selectedAnnotationIds.size > 0;

    // Annotation kind switches — only when all selected items are annotations.
    if (annotationsOnly) {
      const annotations = this.getSelectedAnnotations();
      const allDoors = annotations.every((a) => getOpeningKindProperty(a.identifier).value === OpeningKind.DOOR);
      const allWindows = annotations.every((a) => getOpeningKindProperty(a.identifier).value === OpeningKind.WINDOW);
      if (!allDoors) {
        items.push({
          id: 'convert-to-door',
          iconSvg: ICON_DOOR_SVG,
          tooltip: this.viewer.translationService.t('Convert to door'),
          onClick: () => this.setAnnotationKind(OpeningKind.DOOR),
        });
      }
      if (!allWindows) {
        items.push({
          id: 'convert-to-window',
          iconSvg: ICON_WINDOW_SVG,
          tooltip: this.viewer.translationService.t('Convert to window'),
          onClick: () => this.setAnnotationKind(OpeningKind.WINDOW),
        });
      }
    }

    // No delete item: this tool can't delete wall edges, and new openings are
    // OPENING_SKETCHes that `deleteSelection` doesn't cover — the button read
    // as broken. Deleting stays on the Delete/Backspace keys for the legacy
    // annotation case.
    return items;
  }

  // ─── Actions ────────────────────────────────────────────────────────
  // Auto-detection has been temporarily removed. The wall-click flow no
  // longer materialises openings — it only enters wall focus and hands off
  // to the nested LineTool so the user draws openings manually. The
  // detection service (`DoorWindowDetectionService`) is intentionally left
  // on disk so it can be re-wired later without rebuilding from scratch.

  /**
   * Phase-1 wall click for the skeleton path. Builds a wall-focus spec for
   * the collinear chain, installs the main-view focus + wall plane, and
   * enters phase-2 (nested LineTool) so the user can draw openings on the
   * focused wall.
   *
   * No opening detection / materialization runs from here.
   */
  private focusWallFromEdges(edgeIds: string[]): void {
    if (edgeIds.length === 0) return;
    const wallSpec = this.buildWallFocusSpecForChain(edgeIds);
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (!wallSpec || !mainState) return;
    // Hand the focused chain to the adapter — its commitNew projects the
    // drawn rectangle onto (one of) these edges to build the OPENING_SKETCH.
    const wallSketch = this.getCurrentFloorWallSketch();
    this.doorWindowAdapter.setFocusContext(wallSketch ? { wallSketch, edgeIds: [...edgeIds] } : null);
    this.focusedChainEdgeIds = wallSketch ? [...edgeIds] : [];
    void this.applyWallFocusAndEnterDrawPhase(wallSpec, mainState);
  }

  /**
   * Phase-1 wall click on a room/outline-edge pick.
   *
   * When the floor has a wall sketch, the click MATERIALIZES a loose sketch
   * edge along the pick's centerline (perfectly between the two boundary
   * edges, spanning their perpendicular gap as thickness) and then runs the
   * exact same flow as a skeleton-edge click — selection, widget 3D focus,
   * main wall focus, and OPENING_SKETCH creation bound to a real wallEdgeId.
   * The edge is deliberately NOT connected to the rest of the skeleton; the
   * user can connect it later in the wall editor if they choose.
   *
   * Floors without a sketch annotation keep the legacy spec-only flow.
   */
  private focusWallFromRoomPick(pick: RoomEdgePick): void {
    const floorZ = this.resolveFloorZ();
    if (floorZ === null) return;

    const materializedEdgeId = this.materializeWallEdgeFromRoomPick(pick);
    if (materializedEdgeId) {
      // From here the new edge IS a skeleton wall: reuse the plain-click flow.
      this.setSelection([materializedEdgeId], []);
      this.applyWidgetWallClick();
      void this.focusWallFromEdges([materializedEdgeId]);
      this.hideRoomEdgeOverlay();
      return;
    }

    const ceilingZ = this.currentFloorCeilingPlane?.height ?? floorZ + 3.0;
    const wallSpec = buildWallFocusSpecFromRoomEdgePick(pick, floorZ, ceilingZ, this.currentFloorGroupIdentifier);
    const mainState = this.viewer.renderViews[0]?.viewState;
    if (!mainState) return;
    void this.applyWallFocusAndEnterDrawPhase(wallSpec, mainState);
    // Mirror `applyWidgetWallClick` from the skeleton path: pin the widget's
    // wall-focus tab to this wall so the user gets a persistent 3D view of
    // the selection. Hover-driven preview cleanup happens via the snapshot
    // in `wallFocusPreview`; the click promotes that snapshot to persistent.
    const widgetState = this.getWidgetViewState();
    if (widgetState) {
      focusOnWall3D(widgetState, wallSpec, this.wallFocusPreview);
      this.widgetClickFocusKey = roomPickKey(pick);
      this.lastHoverFocusedEdgeId = this.widgetClickFocusKey;
    }
    this.hideRoomEdgeOverlay();
  }

  /**
   * Materialize a loose wall edge from a room/outline pick on the floor's
   * sketch (shared helper — see RoomEdgeFallback.materializeWallEdgeFromRoomPick).
   * Returns the new edge id, or null when the floor has no sketch annotation
   * to add to (legacy spec-only fallback handles that case).
   */
  private materializeWallEdgeFromRoomPick(pick: RoomEdgePick): string | null {
    const sketch = this.getCurrentFloorWallSketch();
    if (!sketch) return null;
    return materializeLooseWallEdge(this.viewer, sketch, pick);
  }

  /**
   * Shared tail of the two wall-click entry points. Switches the main view to
   * wall-perpendicular focus, swaps the `viewerMode.associatedPlane` so the
   * LineTool unprojects onto the wall, watches for the user navigating away
   * from the focus tab, and enters phase-2 (nested LineTool).
   */
  private async applyWallFocusAndEnterDrawPhase(wallSpec: WallFocusSpec, mainState: NonNullable<(typeof this.viewer.renderViews)[number]['viewState']>): Promise<void> {
    this.activeWallSpec = wallSpec;
    // Remember the view we're entering from so exit returns there (3D→3D,
    // 2D→2D). The GLOBAL ViewManager is the source of truth for the main
    // view's current view — the main RenderViewState's `currentViewId` is
    // seeded once and doesn't track later 2D/3D switches, so reading the
    // wall-focus tab's captured origin would give a stale id.
    const global = this.viewer.personaManager?.viewManager;
    this.wallFocusReturnViewId = global?.getCurrentView()?.id ?? null;
    // Capture the user's TRUE pre-focus state BEFORE the forced X-Ray switch
    // below mutates the camera. The wall-focus tab snaps back to this on exit
    // (pose + roll + view id), so a wall focused from 3D returns to the exact
    // 3D position, and one focused from 2D returns to the saved floor rotation
    // — not the transient X-Ray pose the forced switch installs.
    //
    // The captured snapshot's `currentViewId` is force-set to the global
    // ViewManager's current view (the authoritative source for the main view's
    // 2D/3D state — the main RenderViewState's `currentViewId` is seeded once
    // and doesn't track later switches). `RenderViewState.setView` compares the
    // tab origin's `currentViewId` against the exit target to decide whether to
    // restore the exact pose, so these must agree.
    const wallFocusOrigin = mainState.captureTransientState();
    wallFocusOrigin.currentViewId = this.wallFocusReturnViewId;
    // The wall elevation is always 2D X-Ray. Apply that shader + background the
    // SAME way the view radio button does — by switching the global ViewManager
    // to XRAY_2D so its `onEnter` runs (density mode on, black background). The
    // custom wall view layered on next only re-frames the camera + clip; it
    // doesn't touch material/background, so X-Ray persists.
    //
    // AWAIT this. `switchToView` is async (it runs a 3D→2D camera conversion +
    // onEnter). If we applied the wall framing before it settled, the conversion
    // would land AFTER `focusOnWall` and clobber the wall camera + clip — the
    // "weird state, broken clip" seen only when entering from 3D (from 2D the
    // switch is a no-op early-return, so there was never a race).
    if (global && global.getCurrentView()?.id !== ViewModes.XRAY_2D.id) {
      await global.switchToView(ViewModes.XRAY_2D.id);
    }
    focusOnWall(mainState, wallSpec, this.mainWallFocusPreview, wallFocusOrigin);
    this.installWallPlane(wallSpec);
    // Drop the floor-isolation slab clip while wall focus is active. The
    // wall-focus view installs its own clip box (SHOW_INSIDE on the wall
    // slab) which is the only clip that should be in effect here; the
    // floor isolation's horizontal slab leaks into the screenshot capture
    // path (capture runs outside the per-view override application, so it
    // sees the base clip state), producing a thin horizontal slice in the
    // captured screenshot instead of the full wall slab. Restored on
    // `clearMainWallView` so re-entering the floor view gets isolation back.
    this.suspendFloorIsolation();
    this.wallFocusViewWatcher?.unsubscribe();
    this.wallFocusViewWatcher = mainState.currentViewId.subscribeToChanges((next) => {
      if (next !== WALL_FOCUS_VIEW_ID) {
        this.clearMainWallView();
      }
    });
    this.enterDrawPhase();
  }

  /**
   * Suspend the active floor-isolation clip. Called when entering wall focus
   * so the screenshot capture (which renders outside per-view overrides)
   * doesn't see a horizontal slab clip. The strategy is restored on
   * `resumeFloorIsolation`.
   *
   * The held release-handle also gates `applyFloorIsolation` below: every
   * `objects_selection_changed` event re-fires `FloorplanToolBase.
   * handleSelectionChanged` → `applyFloorIsolation`, which would otherwise
   * undo our suspend the first time the user clicks anything in phase-2.
   *
   * Uses the manager's owner-keyed `acquire` (not bare freeze/unfreeze):
   * release is idempotent, so an exception or double teardown between
   * suspend/resume can't leak or corrupt the manager's freeze refcount.
   * Manager-level freeze, not just `clearCustomStrategy` — the inner
   * LineTool's `onEnable` runs `super.onEnable()` → `applyFloorIsolation`
   * → `setCustomStrategy`, which the freeze turns into a no-op.
   */
  private floorIsolationSuspended = false;
  private suspendFloorIsolation(): void {
    if (this.floorIsolationSuspended) return;
    this.floorIsolationSuspended = true;
    this.viewer.floorIsolationManager.freeze();
  }

  private resumeFloorIsolation(): void {
    if (!this.floorIsolationSuspended) return;
    this.floorIsolationSuspended = false;
    this.viewer.floorIsolationManager.unfreeze();
    // Re-apply this tool's preferred strategy so the floor view comes back
    // with the right slab when we exit wall focus.
    if (this.currentFloorFloorPlane && this.currentFloorGroupIdentifier) {
      this.applyFloorIsolation(this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
    }
  }

  /**
   * Replace `viewerMode.associatedPlane` with a vertical plane through the
   * focused wall. The LineTool's `CoordinateTransformer.screenToWorld`
   * unprojects screen-space mouse onto whatever `associatedPlane` is set to;
   * with the wall plane installed, prompts, segmentation capture corners,
   * and edit handles all land on the wall surface instead of the floor.
   *
   * Plane geometry:
   *   - origin = wall midpoint, mid-height between floor and ceiling.
   *   - normal = the wall-perpendicular direction WallFocus uses to place the
   *     camera (deterministic side selection — see applyWallFocus).
   *
   * The previous `associatedPlane` is snapshotted so it can be restored on
   * `clearMainWallView` (which fires on Esc, Enter, disable, and floor
   * change). The plane is constructed with `ClipTask.NONE` because it's only
   * used as a coordinate-projection reference, not a clipper — the wall
   * focus's BoxVolume already owns the visible-region clip.
   */
  private installWallPlane(spec: WallFocusSpec): void {
    const dx = spec.end.x - spec.start.x;
    const dy = spec.end.y - spec.start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const ux = dx / len;
    const uy = dy / len;
    // Same deterministic perpendicular as applyWallFocus. The exact side
    // doesn't matter for projection — `screenToWorld` works with either —
    // but matching keeps the wall plane co-located with the camera-normal.
    let nx = -uy;
    let ny = ux;
    if (nx < 0 || (nx === 0 && ny < 0)) {
      nx = -nx;
      ny = -ny;
    }
    const midX = (spec.start.x + spec.end.x) / 2;
    const midY = (spec.start.y + spec.end.y) / 2;
    const midZ = (spec.floorZ + spec.ceilingZ) / 2;

    if (!this.wallPlaneActive) {
      this.previousAssociatedPlane = this.viewer.viewerMode.associatedPlane;
      this.wallPlaneActive = true;
    }
    const wallPlane = new PointoramaPlane({
      position: new THREE.Vector3(midX, midY, midZ),
      normal: new THREE.Vector3(nx, ny, 0),
      clipTask: ClipTask.NONE,
    });
    // Stash the wall's in-plane half-extents (along u = wall direction,
    // along v = vertical) + the slab's half-thickness so consumers
    // (DoorWindowEditKindAdapter.getCaptureBoxCorners) can derive a
    // tight oriented bounding box without recomputing the wall geometry.
    const halfU = len / 2 + (spec.margin ?? 0.3);
    const halfV = (spec.ceilingZ - spec.floorZ) / 2 + (spec.margin ?? 0.3);
    const halfN = Math.max(spec.leftSize + spec.rightSize, 0.01) / 2 + (spec.margin ?? 0.3);
    (wallPlane as unknown as { __wallHalfExtents?: { u: number; v: number; n: number } }).__wallHalfExtents = { u: halfU, v: halfV, n: halfN };
    this.viewer.viewerMode.associatedPlane = wallPlane;
  }

  /** Inverse of installWallPlane — restore the previous associatedPlane. */
  private restorePreviousAssociatedPlane(): void {
    if (!this.wallPlaneActive) return;
    this.viewer.viewerMode.associatedPlane = this.previousAssociatedPlane;
    this.previousAssociatedPlane = null;
    this.wallPlaneActive = false;
  }

  private setAnnotationKind(kind: OpeningKind): void {
    const entries: { identifier: string; before: OpeningKind; after: OpeningKind }[] = [];
    for (const m of this.getSelectedAnnotations()) {
      let before: OpeningKind = OpeningKind.WINDOW;
      try {
        before = getOpeningKindProperty(m.identifier).value;
      } catch {
        /* defensive — property service not initialised */
      }
      // Skip no-op flips so undo doesn't accumulate empty steps when the
      // user toggles to the kind that's already set.
      if (before === kind) continue;
      entries.push({ identifier: m.identifier, before, after: kind });
      getOpeningKindProperty(m.identifier).value = kind;
    }
    if (entries.length > 0) {
      this.undoRedo?.pushState(new OpeningKindSnapshot(entries));
    }
    // Rebuild the context menu in place so the just-clicked "Convert to X"
    // button is replaced by the now-meaningful "Convert to Y" — without
    // this, the user sees the same (now-stale) button after a conversion.
    // `buildContextMenuItems` re-evaluates the all-doors / all-windows
    // predicates against the live property values we just wrote.
    const anchor = this.contextMenu?.currentAnchor;
    if (anchor) this.showContextMenuForSelection(anchor);
  }

  /** Delete every selected door/window AreaAnnotation. Returns whether anything was deleted. */
  private deleteSelection(): boolean {
    if (this.selectedAnnotationIds.size === 0) return false;
    const annotations = this.getSelectedAnnotations();
    // Capture full state BEFORE removing — the annotation instance is
    // disposed inside `deleteOpeningFromScene` and its reactive
    // properties go with it. Snapshot also drives Apollo deletion via
    // the `measurement_delete` event so the server doesn't re-sync the
    // openings back on the next round-trip.
    const states: OpeningState[] = [];
    for (const m of annotations) {
      const state = captureOpeningState(m, this.currentFloorGroupIdentifier);
      if (state) states.push(state);
    }
    for (const m of annotations) {
      deleteOpeningFromScene(this.viewer, m.identifier);
    }
    if (states.length > 0) {
      this.undoRedo?.pushState(new OpeningExistenceSnapshot(this.viewer, states, 'absent'));
    }
    this.selectedAnnotationIds.clear();
    this.edgeSelection?.setSelection(null, null);
    return annotations.length > 0;
  }

  // ─── Widget wall focus ──────────────────────────────────────────────

  /**
   * Locate the widget's RenderViewState. The widget RenderView lives on the
   * viewer for the project's lifetime (see `viewer.ensureWidgetView`),
   * independent of whether the React widget component is currently mounted —
   * so state writes via this state survive widget close/open. Returns null
   * only before the widget has been opened for the first time in this
   * session (the persistent view is lazily created on first widget mount).
   *
   * Callers that should NO-OP when the widget is hidden — e.g. hover preview,
   * which shouldn't auto-open the widget — must also check `view.enabled`
   * (`getVisibleWidgetViewState` wraps that check).
   */
  private getWidgetViewState(): RenderViewState | null {
    const views = this.viewer.renderViews;
    for (let i = 1; i < views.length; i++) {
      const vs = views[i].viewState;
      if (vs) return vs;
    }
    return null;
  }

  /**
   * Like `getWidgetViewState`, but returns null when the widget is hidden.
   * Use for visual previews that only make sense when the user can actually
   * see them (hover focus). Persistent state writes (click focus, snapshot
   * restore on tool hand-off) should use `getWidgetViewState` so they land
   * on the widget's state and take effect on the next open.
   */
  private getVisibleWidgetViewState(): RenderViewState | null {
    const views = this.viewer.renderViews;
    for (let i = 1; i < views.length; i++) {
      const v = views[i];
      if (v.viewState && v.enabled) return v.viewState;
    }
    return null;
  }

  /**
   * Build a `WallFocusSpec` framing the full collinear chain `edgeIds`.
   *
   * Endpoints: the two outermost node positions along the chain's average
   * direction (every chain segment is collinear within ~10°, so direction
   * variation across the chain is small enough for a single-axis projection
   * to pick the right pair).
   *
   * Thickness: max `leftSize` and `rightSize` across all chain segments,
   * so the focus box hugs the widest cross-section even when individual
   * segments have different per-side widths.
   *
   * Returns null when any required data (nodes, floor plane) is missing.
   */
  private buildWallFocusSpecForChain(edgeIds: string[]): WallFocusSpec | null {
    const skeleton = this.getCurrentFloorWallSketch();
    if (!skeleton || edgeIds.length === 0) return null;

    // Use the first edge's direction as the projection axis; given the
    // collinearity guarantee, every other edge in the chain is parallel
    // within ~10° and the projection picks the right extremes either way.
    const firstEdge = skeleton.getEdge(edgeIds[0]);
    if (!firstEdge) return null;
    const aFirst = skeleton.getNode(firstEdge.startNodeId);
    const bFirst = skeleton.getNode(firstEdge.endNodeId);
    if (!aFirst || !bFirst) return null;
    const dx = bFirst.position.x - aFirst.position.x;
    const dy = bFirst.position.y - aFirst.position.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return null;
    const ux = dx / len;
    const uy = dy / len;

    let minT = Infinity;
    let maxT = -Infinity;
    let minPos: { x: number; y: number } | null = null;
    let maxPos: { x: number; y: number } | null = null;
    let leftSize = 0;
    let rightSize = 0;
    const seenNodes = new Set<string>();

    for (const edgeId of edgeIds) {
      const e = skeleton.getEdge(edgeId);
      if (!e) continue;
      leftSize = Math.max(leftSize, e.leftSize ?? 0.1);
      rightSize = Math.max(rightSize, e.rightSize ?? 0.1);
      for (const nid of [e.startNodeId, e.endNodeId]) {
        if (seenNodes.has(nid)) continue;
        seenNodes.add(nid);
        const node = skeleton.getNode(nid);
        if (!node) continue;
        // Project node position onto the chain axis (origin = first edge's
        // start). Sign-and-magnitude along ux/uy gives us a 1D coordinate
        // we can min/max over.
        const t = (node.position.x - aFirst.position.x) * ux + (node.position.y - aFirst.position.y) * uy;
        if (t < minT) {
          minT = t;
          minPos = { x: node.position.x, y: node.position.y };
        }
        if (t > maxT) {
          maxT = t;
          maxPos = { x: node.position.x, y: node.position.y };
        }
      }
    }
    if (!minPos || !maxPos) return null;

    const floorZ = this.currentFloorFloorPlane?.height ?? skeleton.getFloorHeight();
    // No ceiling plane on this floor: assume a 3m wall — same fallback the
    // tool uses elsewhere when sizing per-floor extents.
    const ceilingZ = this.currentFloorCeilingPlane?.height ?? floorZ + 3.0;

    return {
      start: new THREE.Vector3(minPos.x, minPos.y, floorZ),
      end: new THREE.Vector3(maxPos.x, maxPos.y, floorZ),
      leftSize: leftSize || 0.1,
      rightSize: rightSize || 0.1,
      floorZ,
      ceilingZ,
      floorGroupId: this.currentFloorGroupIdentifier,
    };
  }

  /**
   * Hover-driven widget update. Throttled to ~30Hz: a fast mousemove across
   * many edges shouldn't rebuild the wall box every frame. Edge-id stability
   * skips no-ops too — re-applying the same focus spec would still reseed the
   * camera mid-orbit.
   *
   * No-op when the widget is closed; the user explicitly asked that hovering
   * not auto-open the widget. Uses `getVisibleWidgetViewState` (rather than
   * `getWidgetViewState`) because the persistent widget RenderViewState
   * exists even while closed, but a hover preview against a hidden widget
   * would do nothing visible while still mutating state. Same for an empty
   * hover (no edge / annotation winning the hit test).
   */
  private updateWidgetWallHover(): void {
    const state = this.getVisibleWidgetViewState();
    if (!state) return;

    // Annotations win the hit-test in this tool — when one is hovered, the
    // skeleton edge / room-edge below it shouldn't influence the widget.
    // Treat that as "no focus" and clear any in-flight preview.
    if (this.hoveredAnnotationId) {
      if (this.lastHoverFocusedEdgeId !== null) {
        clearPreview(state, this.wallFocusPreview);
        this.lastHoverFocusedEdgeId = null;
      }
      return;
    }

    // Build a focus spec + a stable identity key for throttling. WallSketch
    // edges use the collinear chain's sorted-edge-ids; the no-skeleton
    // fallback uses a hash of the picked + opposite edge endpoints so
    // hovering the same wall produces the same key across frames.
    let spec: WallFocusSpec | null = null;
    let key: string | null = null;

    if (this.hoveredEdgeId) {
      const skeleton = this.getCurrentFloorWallSketch();
      if (skeleton) {
        const chain = this.findCollinearChain(skeleton, this.hoveredEdgeId);
        if (chain) {
          key = [...chain.edgeIds].sort().join(',');
          spec = this.buildWallFocusSpecForChain(chain.edgeIds);
        }
      }
    } else if (this.hoveredRoomPick) {
      const floorZ = this.resolveFloorZ();
      if (floorZ !== null) {
        const ceilingZ = this.currentFloorCeilingPlane?.height ?? floorZ + 3.0;
        spec = buildWallFocusSpecFromRoomEdgePick(this.hoveredRoomPick, floorZ, ceilingZ, this.currentFloorGroupIdentifier);
        key = roomPickKey(this.hoveredRoomPick);
      }
    }

    if (key === null || spec === null) {
      if (this.lastHoverFocusedEdgeId !== null) {
        clearPreview(state, this.wallFocusPreview);
        this.lastHoverFocusedEdgeId = null;
      }
      return;
    }

    // The selected wall's 3D perspective view (click focus) stands — hovering
    // it must not downgrade the widget to the ortho hover preview. Hovering a
    // DIFFERENT wall still previews; previewOnWall snapshots the persistent
    // click state and clearPreview restores it on unhover.
    if (key === this.widgetClickFocusKey) {
      return;
    }

    const now = performance.now();
    if (key === this.lastHoverFocusedEdgeId && now - this.lastHoverApplyTime < WALL_HOVER_THROTTLE_MS) {
      return;
    }
    previewOnWall(state, spec, this.wallFocusPreview);
    this.lastHoverFocusedEdgeId = key;
    this.lastHoverApplyTime = now;
  }

  /**
   * Click-driven widget update. Persistent focus when exactly ONE WALL —
   * a single collinear chain of edges (a plain click selects the whole
   * strip, so the selection usually holds several segment ids) — and no
   * annotations is selected; clears focus otherwise. Discards any active
   * hover snapshot so unhover doesn't undo the click.
   *
   * Click uses the 3D-perspective variant — the main view already owns the
   * orthographic wall-focus card after `focusWallFromEdges`, so the widget
   * showing the same flat elevation would be redundant. Same wall slab,
   * same clip box, different camera angle.
   */
  private applyWidgetWallClick(): void {
    // The hover strip's job is done once the click lands — and while a wall
    // is selected the mousemove gate stops refreshing it, so an overlay left
    // visible here would just linger. Hover re-shows it after deselection.
    this.hideRoomEdgeOverlay();
    const state = this.getWidgetViewState();
    if (!state) return;

    if (this.selectedAnnotationIds.size === 0 && this.selectedEdgeIds.size > 0) {
      const selectedIds = [...this.selectedEdgeIds];
      const skeleton = this.getCurrentFloorWallSketch();
      const chain = skeleton ? this.findCollinearChain(skeleton, selectedIds[0]) : null;
      const chainEdgeIds = chain?.edgeIds ?? [selectedIds[0]];
      const chainSet = new Set(chainEdgeIds);
      const isSingleWall = selectedIds.every((id) => chainSet.has(id));

      if (isSingleWall) {
        const spec = this.buildWallFocusSpecForChain(chainEdgeIds);
        if (spec) {
          focusOnWall3D(state, spec, this.wallFocusPreview);
          // Key the click focus the same way the hover path keys walls
          // (sorted collinear-chain ids) so hovering the selected wall is
          // recognized as "same wall" and doesn't stomp the 3D view with
          // the ortho preview.
          this.widgetClickFocusKey = [...chainEdgeIds].sort().join(',');
          this.lastHoverFocusedEdgeId = this.widgetClickFocusKey;
          return;
        }
      }
    }

    // Multi-wall, annotation, or empty selections: no widget focus.
    this.clearWidgetWallFocus();
  }

  /**
   * Wipe both transient and persistent widget focus. Called on tool disable,
   * floor change, and selection-change-to-non-single-edge. Safe when no
   * widget is mounted (preview snapshot stays cleared either way). Removes
   * the wall-focus custom view tab outright — the tool owns it, so when no
   * focus is desired the tab should not linger in the switcher.
   */
  private clearWidgetWallFocus(): void {
    const state = this.getWidgetViewState();
    if (state) {
      clearPreview(state, this.wallFocusPreview);
      // clearPreview only auto-removes transient tabs; a persistent one
      // (left over from a click) still needs explicit cleanup.
      // `removeCustomView` handles restoring the destination view's
      // shader/EDL/camera state via the `applyView` call we added to
      // `removeCustomViewEntry` — no further override resets needed here.
      if (state.getCustomView(WALL_FOCUS_VIEW_ID)) {
        state.removeCustomView(WALL_FOCUS_VIEW_ID);
      }
    }
    this.wallFocusPreview.innerSnapshot = null;
    this.widgetClickFocusKey = null;
    this.lastHoverFocusedEdgeId = null;
    this.lastHoverApplyTime = 0;
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  private getSelectedAnnotations(): OpeningAnnotation[] {
    const out: OpeningAnnotation[] = [];
    for (const m of this.viewer.scene.measurements) {
      if (!(m instanceof OpeningAnnotation)) continue;
      if (this.selectedAnnotationIds.has(m.identifier)) out.push(m);
    }
    return out;
  }

  private getCurrentFloorWallSketch(): PointoramaWallSketch | null {
    if (!this.currentFloorGroupIdentifier) return null;
    return this.viewer.scene.wallSketches.find((s) => s.groupIdentifier === this.currentFloorGroupIdentifier) || null;
  }

  /**
   * Project the cursor onto the current floor's height plane to get a
   * world-space anchor for the context menu and the room-edge fallback's
   * perpendicular foot. Prefers the skeleton's floor height when available,
   * falls back to `currentFloorFloorPlane.height` so the no-skeleton path
   * still gets a usable anchor. Returns null only when neither source
   * resolves a floor Z.
   */
  private cursorWorldAnchor(event: MouseEvent): THREE.Vector3 | null {
    const z = this.resolveFloorZ();
    if (z === null) return null;
    const camera = this.viewer.scene.getActiveCamera();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
      raycaster.ray.origin.addScaledVector(raycaster.ray.direction, camera.near);
    }
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -z);
    const hit = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, hit) ? hit : null;
  }

  /** Floor Z for the active floor — skeleton's height first, then the floor plane. */
  private resolveFloorZ(): number | null {
    const skel = this.getCurrentFloorWallSketch();
    if (skel) return skel.getFloorHeight();
    const fp = this.currentFloorFloorPlane;
    return fp ? fp.height : null;
  }
}

/**
 * Stable identity key for a `RoomEdgePick`, used by the widget hover throttle.
 * Quantises the endpoint coordinates to 3 decimals (millimetre precision) so
 * sub-millimetre cursor jitter on the same wall still hashes to the same key
 * and the throttle skip kicks in. Includes the opposite edge (or `'ext'`
 * marker for the exterior-wall fallback) so two adjacent walls sharing one
 * picked edge produce distinct keys.
 */
function roomPickKey(pick: RoomEdgePick): string {
  const q = (n: number) => n.toFixed(3);
  const a = pick.pickedEdge.a;
  const b = pick.pickedEdge.b;
  const opp = pick.oppositeEdge;
  const oppKey = opp ? `${q(opp.a.x)},${q(opp.a.y)},${q(opp.b.x)},${q(opp.b.y)}` : 'ext';
  return `room:${q(a.x)},${q(a.y)},${q(b.x)},${q(b.y)}|${oppKey}`;
}
